/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { Controller, useForm } from 'react-hook-form';
import type { FieldErrors, Resolver } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Divider,
  Link,
  Typography,
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableContainer,
  Switch,
  Collapse,
  Stepper,
  Step,
  StepButton,
  Checkbox,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HELP_FAQ_AUTO_CLASSIFICATION } from '../../../constants/externalUrls';
import { formLabelWrapper, regularLg } from '../../../constants/location';

import {
  gray5,
  gray10,
  gray100,
  gray80,
  gray60,
  gray110,
  gray30,
  grayBlue50,
  grayBlue40,
  grayBlue30,
  orange_overlay20,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
  gray40,
  orange50,
  navy50,
  yellow50,
  yellow_overlay10,
} from '../../../constants/colors';
import {
  boldMd,
  boldSm,
  boldXxl,
  regularSm,
  mediumLg,
  spHeaderTitle,
} from '../../../constants/location';
import { patternRegisterSchema } from '../../../validation/pattern';

import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import PatternFileUploader from '../../uiParts/form/PatternFileUploader';
import SideBar from '../../uiParts/menu/SideBar';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../hooks/usePageLayout';
import MetaTitle from '../../utils/MetaTitle';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomSelectField from '../../uiParts/form/CustomSelectField';
import OutputTypeSelector from '../../uiParts/form/OutputTypeSelector';
import ItemListManager from '../../uiParts/parts/ItemListManager';
import DeletedBadge from '../../uiParts/parts/DeletedBadge';
import { postPattern } from '../../../api/firebase/firestore/pattern/create';
import {
  getCompanyRepresentative,
  type CompanyRepresentative,
} from '../../../api/firebase/firestore/company/getCompanyRepresentative';
import {
  getPattern,
  getPatternCountByCompanyId,
} from '../../../api/firebase/firestore/pattern/read';
import { updatePattern } from '../../../api/firebase/firestore/pattern/update';
import {
  createPatternHistory,
  getLatestPatternHistory,
  getUsedItemIdsFromHistory,
  hasPatternHistoryDiff,
  toPatternHistorySnapshot,
} from '../../../api/firebase/firestore/pattern/history';
import {
  PatternFieldType,
  PatternFileType,
  ItemListChildType,
  ItemListType,
} from '../../../types/pattern';
import { InputFileType } from '../../../types/input';
import { MasterWithMappingType } from '../../../types/master';
import { DictionaryWithMappingType } from '../../../types/dictionary';
import { useCompanyGroups } from '../../../hooks/useCompanyGroups';
import { handleOcrItemList } from '../../../api/firebase/function/handleGetItemList';
import { resolvePatternFileUrl } from '../../../api/firebase/storage';
import {
  convertPdfToImages,
  isPdfFile,
  splitPdfIntoPages,
  isFirstInSplitGroup,
  getSplitGroupIndices,
  isSplitPageFile,
  getSplitSiblings,
  capFilesToTotalPages,
  getPdfPageCountFromUrl,
} from '../../../utils/pdfToImage';
import ItemDeleteModal from '../../uiParts/modal/ItemDeleteModal';
import ItemDeleteBlockedModal from '../../uiParts/modal/ItemDeleteBlockedModal';
import { DeleteItemData as ItemListDeleteItemData } from '../../uiParts/parts/ItemListManager';
import LoadingModal from '../../uiParts/modal/LoadingModal';
import PatternItemOverwriteConfirmModal from '../../uiParts/modal/PatternItemOverwriteConfirmModal';
import PatternCompleteModal from '../../uiParts/modal/PatternCompleteModal';
import {
  fileProcessingType,
  geminiModel,
  displayWidth,
  planLimits,
  patternStatus,
  patternStep,
  shareScope,
  PATTERN_SAMPLE_MAX_PAGES,
  toAvailableGeminiModel,
  GEMINI_MODEL_DEFAULT,
  ocrEngineType,
  ocrEngineChoiceForOperator,
} from '../../../constants/enum';
import { useCompanyUsers } from '../../../hooks/useCompanyUsers';
import { generatePromptFromItemList } from '../../../utils/generatePromptFromItemList';
import { convertItemListToGeminiPrompt } from '../../../utils/convertItemListToGeminiPrompt';
import {
  getFormatDataPromptForPattern,
  DEFAULT_DATE_EXTRACTION_RULE,
} from '../../../constants/promptTemplates';
import { normalizeItemListIds } from '../../../utils/normalizeItemListIds';
import {
  collectDeletionTargetItemIds,
  collectItemIds,
  findItemReferences,
  formatItemReferences,
  getBlockingReferences,
  hasBlockingReference,
  type ItemReference,
} from '../../../utils/pattern/findItemReferences';
import { cleanupItemReferences } from '../../../api/firebase/firestore/pattern/cleanupItemReferences';
import { getMastersByPatternId } from '../../../api/firebase/firestore/master/readByPattern';
import { getDictionariesByPatternId } from '../../../api/firebase/firestore/dictionary/readByPattern';
import {
  getOcrTargetItemCount,
  hasInvalidTypes,
  hasEmptyItems,
  isItemCountExceeded,
  getItemCountExceededMessage,
  getMaxItemCount,
  getCalculationSettingsError,
  findInvalidChoiceItem,
} from '../../../utils/pattern/validation';
import {
  fillMissingFieldCodes,
  validateFieldCodesInItemList,
  fieldCodeErrorMessages,
} from '../../../utils/pattern/fieldCode';
import { isOperatorStaff, isCompanyAdmin } from '../../../utils/permission';
import { useCompanyId } from '../../../hooks/useCompanyId';
import {
  PROXY_BANNER_HEIGHT,
  GROUP_EMPTY_MESSAGE,
  AUTO_CLASSIFY_HELP_LABEL,
} from '../../../constants/labels';
import { getOcrCreditRateWithModel } from '../../../constants/plans';
import useSnackbar from '../../../hooks/useSnackbar';
import { useTotalPageCount } from '../../../hooks/useTotalPageCount';
import { TUTORIAL_VIDEO_URL } from '../../../constants/externalUrls';
import { patternMessages } from '../../../constants/messages';
import TutorialModal from '../../uiParts/modal/TutorialModal';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../api/firebase/config';
import { getCreditRateByPlan } from '../../../utils/userUsageLimit';
import { useUploadDispatch } from '../../../contexts/UploadContext';
import ExportTemplateManager, {
  type PendingExportTemplateChanges,
} from '../../uiParts/parts/ExportTemplateManager';
import {
  commitExportTemplates,
  hasPendingExportTemplateChanges,
} from '../../../utils/template/commitExportTemplates';

// ステップ定義
const STEPS = ['アップロード', '項目設定', '設定・保存'];

/**
 * URLの ?step= から編集時の初期表示ステップを決める（#3147）
 * 未指定・数値でない・範囲外の場合は項目設定（Step2）にフォールバックする。
 * Step1から始めるとAI読取に誘導されやすく、意図せず既存の項目設定が置き換わるため。
 */
const resolveInitialStep = (stepParam: string | null): number => {
  const defaultStep = patternStep.itemSetting;
  if (stepParam === null) return defaultStep;
  const parsed = Number(stepParam);
  if (!Number.isInteger(parsed)) return defaultStep;
  if (parsed < patternStep.upload || parsed > patternStep.settingSave) return defaultStep;
  return parsed;
};

const styles = {
  formLabelWrapper,
  headerLink: {
    ...regularSm,
    color: orange50,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: 0,
    '&:hover': { opacity: 0.8 },
  },
  headerTitle: {
    ...boldXxl,
    color: gray100,
    fontWeight: 500,
    lineHeight: 1.2,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
      fontWeight: 500,
      lineHeight: 1.2,
    },
  },
  contentAreaBackground: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '852px',
    margin: '0 auto',
    minHeight: 'calc(100vh - 64px)',
    padding: '0 0 48px 0',
    '@media screen and (max-width:960px)': {
      minHeight: 'calc(100vh - 48px)',
      padding: '16px 0 48px 0',
    },
  },
  contentAreaBackgroundWide: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    margin: '0 auto',
    minHeight: 'calc(100vh - 64px)',
    padding: '0 0 48px 0',
    '@media screen and (max-width:960px)': {
      minHeight: 'calc(100vh - 48px)',
      padding: '16px 0 48px 0',
    },
  },
  contentAreaBackgroundCompact: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '852px',
    margin: '0 auto',
    flex: 1,
    padding: '0 0 24px 0',
    '@media screen and (max-width:960px)': {
      padding: '16px 0 24px 0',
    },
  },
  stepperContainer: {
    width: '100%',
    maxWidth: '852px',
    margin: '0 auto 24px auto',
    '@media screen and (max-width:960px)': {
      padding: '0 16px',
      boxSizing: 'border-box',
    },
  },
  stepper: {
    '& .MuiStepLabel-label': {
      fontSize: '14px',
      fontWeight: 500,
    },
    '& .MuiStepLabel-label.Mui-active': {
      fontWeight: 700,
    },
    '& .MuiStepLabel-label.Mui-completed': {
      fontWeight: 500,
    },
    '& .MuiStepIcon-root': {
      color: grayBlue30,
    },
    '& .MuiStepIcon-root.Mui-active': {
      color: gray110,
    },
    '& .MuiStepIcon-root.Mui-completed': {
      color: gray110,
    },
  },
  pagination: {
    '& .MuiPagination-ul .Mui-selected': {
      backgroundColor: orange_overlay20,
    },
  },
  uploadCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  uploadCancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  uploadSubmitButton: {
    height: '44px',
    width: '100%',
    maxWidth: '320px',
    margin: '0 auto',
    display: 'block',
    ...primaryButtonBackgroundColor,
    '@media (max-width: 960px)': {
      width: '100%',
      maxWidth: 'none',
    },
  },
  uploadSubmitButtonText: {
    ...boldSm,
    color: white,
  },
  deliveryMainInfo: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '784px',
    width: '100%',
    margin: '0 auto',
    padding: '0',
    '@media (max-width: 960px)': {
      margin: '0px 0px 8px 0px',
      padding: '0 16px 0px 16px',
      flexDirection: 'column',
      boxSizing: 'border-box',
    },
  },
  deliveryMainInfoWide: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    margin: '0',
    padding: '0',
    '@media (max-width: 960px)': {
      margin: '0px 0px 8px 0px',
      padding: '0 16px 0px 16px',
      flexDirection: 'column',
      boxSizing: 'border-box',
    },
  },
  itemSettingContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '32px',
    marginTop: '0',
    marginBottom: '0',
    marginLeft: '32px',
    marginRight: '32px',
    minHeight: '700px',
    '@media (max-width: 1200px)': {
      flexDirection: 'column',
      gap: '16px',
      minHeight: 'auto',
      marginBottom: '0',
      marginLeft: '16px',
      marginRight: '16px',
    },
  },
  imageDisplayColumn: {
    width: '50%',
    '@media (max-width: 1200px)': {
      width: '100%',
    },
  },
  itemListColumn: {
    width: '50%',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray30}`,
    padding: '24px',
    boxSizing: 'border-box',
    '@media (max-width: 1200px)': {
      width: '100%',
      padding: '16px',
    },
  },
  mainContainer: {
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    maxWidth: '784px',
    boxSizing: 'border-box',
    backgroundColor: white,
    margin: '0 auto',
    padding: '20px 32px',
    borderRadius: '4px',
    border: `1px solid ${gray30}`,
    '@media screen and (max-width:960px)': {
      margin: '0',
      width: '100%',
      padding: '16px',
      overflowX: 'auto',
    },
  },
  mainContainerWide: {
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    boxSizing: 'border-box',
    margin: '0 auto',
    padding: '0',
    '@media screen and (max-width:960px)': {
      margin: '0',
      width: '100%',
      padding: '0',
      overflowX: 'auto',
    },
  },
  tableContainer: {
    width: 'auto',
    maxWidth: '100%',
    overflowX: 'auto',
    padding: '0px 0px 0px 10px',
  },
  TableCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px 8px 0',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    minWidth: '35px',
    maxWidth: '140px',
    '@media screen and (max-width:960px)': {
      maxWidth: '130px',
    },
  },
  TableRow: {
    borderBottom: `1px solid ${gray40}`,
    '& td': {
      borderBottom: 'none',
    },
  },
  ButtonCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px 8px 0',
    width: '44px',
  },
  draftButtonText: {
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
  },
  cancelButton: {
    color: gray60,
    height: '24px',
    width: '24px',
    minWidth: '0',
    padding: '0',
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      transform: 'none',
    },
  },
  label: {
    fontSize: '14px',
    display: 'flex',
  },
  sectionTitle: {
    ...boldXxl,
    color: gray100,
    marginBottom: '24px',
    textAlign: 'left',
    position: 'relative',
    paddingLeft: '32px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '4px',
      height: '24px',
      backgroundColor: primaryButtonBackgroundColor.backgroundColor,
      borderRadius: '2px',
    },
    '@media screen and (max-width:960px)': {
      ...boldMd,
      paddingLeft: '24px',
      '&::before': {
        width: '3px',
        height: '20px',
      },
    },
  },
  subSectionTitle: {
    ...regularLg,
    color: gray100,
    marginBottom: '24px',
    textAlign: 'left',
    position: 'relative',
    paddingLeft: '32px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '4px',
      height: '24px',
      backgroundColor: primaryButtonBackgroundColor.backgroundColor,
      borderRadius: '2px',
    },
    '@media screen and (max-width:960px)': {
      ...boldSm,
      paddingLeft: '24px',
      '&::before': {
        width: '3px',
        height: '20px',
      },
    },
  },
  sectionTitleSimple: {
    ...mediumLg,
    color: gray110,
    marginBottom: '16px',
    textAlign: 'left',
    '@media screen and (max-width:960px)': {
      ...boldSm,
    },
  },
  imageDisplayArea: {
    backgroundColor: gray10,
    padding: '0 0 16px 0',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '300px',
    marginBottom: '16px',
    borderRadius: '4px',
    border: `1px solid ${gray30}`,
    overflow: 'hidden',
    '@media screen and (max-width:1200px)': {
      width: 'auto',
      maxHeight: '60vh',
      minWidth: 'auto',
      marginBottom: '16px',
    },
    '@media screen and (max-width:960px)': {
      maxHeight: '50vh',
    },
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    overflow: 'auto',
    width: '80%',
    height: '100%',
    margin: '0 auto',
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      objectFit: 'contain',
    },
  },
  noImageText: {
    color: gray60,
    fontSize: '14px',
    textAlign: 'center',
  },
  imageNavigation: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    backgroundColor: white,
    height: '56px',
    padding: '0 16px',
    borderBottom: `1px solid ${gray30}`,
  },
  imageNavigationText: {
    color: grayBlue40,
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'center',
    flex: 1,
    '@media screen and (max-width:960px)': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  navigationButton: {
    minWidth: 'auto',
    height: '32px',
    padding: '4px 8px',
    color: orange50,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    '&:hover': {
      backgroundColor: 'rgba(255, 107, 53, 0.08)',
    },
    '&:disabled': {
      color: gray40,
    },
  },
  navigationButtonText: {
    color: orange50,
    fontSize: '14px',
    fontWeight: 700,
    '@media screen and (max-width:600px)': {
      display: 'none',
    },
  },
  navigationPlaceholder: {
    width: '120px',
    '@media screen and (max-width:600px)': {
      width: '40px',
    },
  },
  fixedFooter: {
    position: 'fixed' as const,
    bottom: 0,
    right: 0,
    height: '70px',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    zIndex: 1000,
    boxShadow: '0px -2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'left 0.3s ease',
    '@media (max-width: 1200px)': {
      left: 0,
    },
    '@media (max-width: 960px)': {
      padding: '0 16px',
      boxSizing: 'border-box',
    },
    '@media (max-width: 600px)': {
      padding: '0 8px',
      gap: '8px',
    },
  },
  primaryFooterButton: {
    backgroundColor: navy50,
    color: white,
    '&:hover': {
      backgroundColor: navy50,
      opacity: 0.9,
    },
    '&.Mui-disabled': {
      backgroundColor: navy50,
      opacity: 0.5,
      color: white,
    },
    height: '36px',
    padding: '0 16px',
    minWidth: '240px',
    '@media (max-width: 600px)': {
      minWidth: 'auto',
      flex: 1,
      padding: '0 8px',
    },
  },
  secondaryFooterButton: {
    height: '36px',
    padding: '0 12px',
    minWidth: '160px',
    ...tertiaryButtonBackgroundColor,
    '@media (max-width: 600px)': {
      minWidth: 'auto',
      flex: 1,
      padding: '0 8px',
    },
  },
};

// 読取パターン作成時の合計ページ数上限（サンプル上限と同一。二重管理を避け #2961 の定数を参照）
const MAX_TOTAL_PAGES_FOR_REGISTER = PATTERN_SAMPLE_MAX_PAGES;

const PatternAdd = (): ReactElement => {
  // Stepper用ステート
  const [activeStep, setActiveStep] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<{ [k: number]: boolean }>({});

  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(!!id);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const [patternCompleteModalOpen, setPatternCompleteModalOpen] = useState<boolean>(false);
  const [uploadedFilesForOcr, setUploadedFilesForOcr] = useState<File[]>([]);
  const isSaveCompletedRef = useRef<boolean>(false);
  const [tutorialModalOpen, setTutorialModalOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, company, isLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode, operatorId, operatorRole } = useCompanyId();
  const { startUpload } = useUploadDispatch();
  const [baseRemainingCount, setBaseRemainingCount] = useState<number | null>(null);
  const [allowAiLearning, setAllowAiLearning] = useState<boolean>(true);
  // 代理モード時の初回OCRのデフォルト担当者（#2928）
  // 運営ユーザーは代理先企業のメンバーではないため担当者にせず、代理先の企業担当者を解決しておく
  const [proxyDefaultAssignee, setProxyDefaultAssignee] = useState<CompanyRepresentative | null>(
    null,
  );
  const [currentPatternId, setCurrentPatternId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayedFiles, setDisplayedFiles] = useState<InputFileType[]>([]);
  const [existingFiles, setExistingFiles] = useState<PatternFileType[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [useCustomPrompt, setUseCustomPrompt] = useState<boolean>(false);
  const [isClassificationTarget, setIsClassificationTarget] = useState<boolean>(true);
  // 共有範囲（#2804 Phase2）: 選択グループが空なら企業全体、1件以上ならグループ限定
  const [selectedSharedGroupIds, setSelectedSharedGroupIds] = useState<string[]>([]);
  // 企業のグループ一覧（共通フック）。表示は権限で絞る（後述の availableGroups）
  const { groups: companyGroups } = useCompanyGroups(effectiveCompanyId || user?.company_id);
  // 運営・企業管理者は全グループ、一般メンバーは自分が所属するグループのみ選択可能（#2804）
  const availableGroups = useMemo(() => {
    if (isOperatorStaff(user?.role) || isCompanyAdmin(user?.role)) {
      return companyGroups;
    }
    const myGroupIds = user?.group_ids ?? [];
    return companyGroups.filter(g => myGroupIds.includes(g.id));
  }, [companyGroups, user?.role, user?.group_ids]);
  // 共有範囲（担当者指定 #2963）: 選択担当者が空でもグループが空なら企業全体、いずれか1件以上で限定共有
  const [selectedSharedUserIds, setSelectedSharedUserIds] = useState<string[]>([]);
  // 共有先候補の企業メンバー（有効ユーザー）を共通フックで取得（#2963）
  const { users: availableUsers } = useCompanyUsers(effectiveCompanyId || user?.company_id);
  const [showPromptInput, setShowPromptInput] = useState<boolean>(false);
  const [isDeletedPattern, setIsDeletedPattern] = useState<boolean>(false);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const [convertedPdfImages, setConvertedPdfImages] = useState<
    Map<string, { dataUrl: string; width: number; height: number; page: number }[]>
  >(new Map());
  // PDF変換の進行中／失敗済みファイル名。レンダー中に呼ばれる変換の多重起動と、
  // 失敗時の無限リトライを防ぐ。表示の更新には使わないので ref で保持する
  const convertingPdfRef = useRef<Set<string>>(new Set());
  const failedPdfRef = useRef<Set<string>>(new Set());
  // 変換に失敗したファイル。エラー表示の切り替えに使うため state で持つ
  const [pdfConvertFailedFiles, setPdfConvertFailedFiles] = useState<Set<string>>(new Set());
  // 編集時の既存項目IDを保持（新規作成時は空配列）
  const [existingItemIds, setExistingItemIds] = useState<string[]>([]);
  // 項目設定コンテナへの参照
  const itemSettingContainerRef = useRef<HTMLDivElement>(null);
  // Geminiモデル選択の状態管理
  const [selectedGeminiModel, setSelectedGeminiModel] = useState<number>(GEMINI_MODEL_DEFAULT);
  // OCRエンジン選択の状態管理（#3264 運営スタッフ限定）。
  // 未設定のパターンは Vision AI として扱う（従来動作）
  const [selectedOcrEngine, setSelectedOcrEngine] = useState<number>(ocrEngineType.visionAi);

  // ファイルアップロードバリデーションエラー
  const [fileUploadError, setFileUploadError] = useState<string | undefined>(undefined);
  // 指定フォーマット出力（#3070）の未保存変更。パターン保存時にまとめて反映する
  const [pendingExportTemplates, setPendingExportTemplates] =
    useState<PendingExportTemplateChanges>({
      added: [],
      removedIds: [],
      replaced: [],
      updatedSheets: [],
    });
  // 元のPDFファイルを保持（fileType切り替え時に復元するため）
  const [originalPdfFiles, setOriginalPdfFiles] = useState<Map<string, File>>(new Map());

  // 登録ボタンが一度押されたかどうか（初回押下後にバリデーションチェックを行う）
  const [isSubmitAttempted, setIsSubmitAttempted] = useState<boolean>(false);

  // AI項目読取中のローディングモーダル
  const [itemReadingModalOpen, setItemReadingModalOpen] = useState<boolean>(false);
  const [itemReadingProgress, setItemReadingProgress] = useState<number>(0);
  const [itemReadingMessage, setItemReadingMessage] =
    useState<string>('読取パターンを作成しています...');
  // 「設定・保存へ」ボタン押下後にバリデーションを有効化するフラグ
  const [itemListValidated, setItemListValidated] = useState<boolean>(false);

  // ItemListManager用削除モーダルのstate
  const [itemListDeleteModalOpen, setItemListDeleteModalOpen] = useState<boolean>(false);
  const [itemListDeleteItemData, setItemListDeleteItemData] =
    useState<ItemListDeleteItemData | null>(null);
  // 削除しようとした項目を参照しているマスタ照合・辞書変換の設定（#3344）
  const [itemListDeleteReferences, setItemListDeleteReferences] = useState<ItemReference[]>([]);
  // 照合キーとして使われている項目の削除をブロックする案内モーダル（#3344）
  const [itemDeleteBlockedModalOpen, setItemDeleteBlockedModalOpen] = useState<boolean>(false);
  // パターンに紐づくマスタ照合・辞書変換の設定（#3344）。項目削除時の参照判定に使う。
  // N+1 クエリになるため項目削除のたびに取得せず、画面初期化時に1回だけ取得して保持する。
  const [patternMasters, setPatternMasters] = useState<MasterWithMappingType[]>([]);
  const [patternDictionaries, setPatternDictionaries] = useState<DictionaryWithMappingType[]>([]);

  // 編集時の項目置き換え確認モーダルのstate（#3147）
  // 実行対象（Step1のAI読取／Step2の再読取／手動入力リセット）を保持し、確認後にそちらを実行する
  const [itemOverwriteConfirmTarget, setItemOverwriteConfirmTarget] = useState<
    'initial' | 'reRead' | 'reset' | null
  >(null);

  // 編集モード（既存の読取パターンを開いている状態）かどうか
  // 編集時のAI読取は既存項目のIDを振り直すため、実行前に確認を挟む（#3147）
  const isEditMode = !!id;

  // AI読取の導線（STEP1のボタン・STEP2の再読取）を表示するか
  // 運営がカスタムプロンプトを設定している企業の一般ユーザーには非表示
  const canUseAiItemRead = !(useCustomPrompt && !isOperatorStaff(user?.role));

  // URLの ?step= から初期ステップを復元し終えたか（#3147）
  // 復元前に同期を走らせるとURLのstepを初期値0で潰してしまうため、完了までURL書き込みを止める
  const initialStepRestoredRef = useRef<boolean>(false);

  // 表示中のステップをURLの ?step= に同期する（#3147）
  // リロードやURL共有で同じステップを開き直せるようにする。
  // 編集時のみ対象（新規作成時はデータが未保存でURLを共有しても復元できないため）。
  // 依存は step の値のみにする。searchParams オブジェクトを依存に入れると
  // 他のパラメータ変更でも実行され、無駄な履歴操作が起きるため。
  const stepParamValue = searchParams.get('step');
  useEffect(() => {
    if (!isEditMode) return;
    if (!initialStepRestoredRef.current) return;
    if (stepParamValue === String(activeStep)) return;
    setSearchParams(
      prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set('step', String(activeStep));
        return nextParams;
      },
      // 履歴を積むとブラウザバックがステップ移動になり戻りづらくなるため置換する
      { replace: true },
    );
  }, [activeStep, isEditMode, stepParamValue, setSearchParams]);

  // ページタイトルを取得する関数
  const getPageTitle = (): string => {
    const copyId = searchParams.get('copy');
    if (id) {
      const currentTitle = watch('title');
      return currentTitle ? `${currentTitle} - 読取パターン編集` : '読取パターン編集';
    } else if (copyId) {
      return '読取パターン作成（コピー）';
    } else {
      return '読取パターン作成';
    }
  };

  // Stepper用ヘルパー関数
  const handleStep = (step: number) => (): void => {
    // STEP3（結果確認）またはSTEP4（設定・保存）への遷移時チェック
    if (step >= 2) {
      const allCurrentFiles = [...existingFiles, ...(currentUploadedImages || [])];
      // ファイル0個チェック
      if (allCurrentFiles.length === 0) {
        setErrorText(patternMessages.noFileSelectedReturnToFirstStep);
        setSnackbarOpen(true);
        return;
      }
    }
    setActiveStep(step);
  };

  const handleNext = async (): Promise<void> => {
    // Step 2（項目設定）からStep 3（設定・保存）へ進む際のバリデーション
    if (activeStep === 1) {
      // ファイルが1つもない場合はエラー
      const totalFileCount = existingFiles.length + (currentUploadedImages?.length || 0);
      if (totalFileCount === 0) {
        setErrorText(patternMessages.noFileSelectedReturnToFirstStep);
        setSnackbarOpen(true);
        return;
      }

      const itemList = watch('itemList');

      // 項目リストのバリデーション
      if (!itemList || itemList.length === 0) {
        setErrorText('取得したい項目を最低1つ追加してください。');
        setSnackbarOpen(true);
        return;
      }

      // 項目数制限チェック（OCR対象項目数で判定。#3045）
      if (isItemCountExceeded(itemList)) {
        const ocrTargetCount = getOcrTargetItemCount(itemList);
        setErrorText(getItemCountExceededMessage(ocrTargetCount));
        setSnackbarOpen(true);
        return;
      }

      // react-hook-formのバリデーションを発火（各項目に赤枠・エラーメッセージを表示）
      const isFormValid = await trigger('itemList');
      setItemListValidated(true);

      if (!isFormValid) {
        const hasEmptyError = hasEmptyItems(itemList);
        const hasTypeError = hasInvalidTypes(itemList);
        if (hasEmptyError && hasTypeError) {
          setErrorText('項目名が空の項目、またはデータ型が未選択の項目があります。');
        } else if (hasEmptyError) {
          setErrorText('項目名が空の項目があります。すべての項目名を入力してください。');
        } else if (hasTypeError) {
          setErrorText(
            'データ型が未選択の項目があります。すべての項目のデータ型を選択してください。',
          );
        } else {
          setErrorText('入力内容にエラーがあります。各項目を確認してください。');
        }
        setSnackbarOpen(true);
        return;
      }

      // 選択肢の設定チェック（#3075）。
      // 項目リストの行から型だけを変更すると選択肢が空のままになり、
      // そのまま進むとAIに提示する候補がないままOCRが走ってしまうため、ここで弾く
      const choiceError = findInvalidChoiceItem(itemList);
      if (choiceError) {
        setErrorText(`「${choiceError.itemName}」${choiceError.errorMessage}`);
        setSnackbarOpen(true);
        return;
      }
      // フィールドコード（#3175）と計算フィールド（#3145）の検証。
      // フィールドコード未設定の項目は保存時の自動採番（fillMissingFieldCodes）と同じ値で補完してから検証する
      const filledItemList = fillMissingFieldCodes(itemList);
      const fieldCodeError = validateFieldCodesInItemList(filledItemList);
      if (fieldCodeError) {
        setErrorText(
          `「${fieldCodeError.itemName}」のフィールドコードに問題があります。${fieldCodeErrorMessages[fieldCodeError.error]}`,
        );
        setSnackbarOpen(true);
        return;
      }
      const calculationError = getCalculationSettingsError(filledItemList);
      if (calculationError !== null) {
        setErrorText(calculationError);
        setSnackbarOpen(true);
        return;
      }
    }

    setCompletedSteps(prev => ({ ...prev, [activeStep]: true }));
    setActiveStep(prevActiveStep => prevActiveStep + 1);

    // ステップ遷移時にページトップにスクロール
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = (): void => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);

    // ステップ遷移時にページトップにスクロール
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ItemListManager用削除モーダルのハンドラ
  // マスタ照合・辞書変換から参照されている項目は、参照箇所に応じて挙動を分ける（#3344）:
  //   - 照合キー   : 削除をブロックし、先にマスタ側の設定を外してもらう
  //   - それ以外   : 警告つきの削除確認モーダルを出し、保存時に該当参照のみ解除する
  const handleItemListDeleteRequest = (data: ItemListDeleteItemData): void => {
    // 親項目を削除すると子の item_id も消えるため、子のIDまで判定対象に含める
    const targetItemIds =
      data.parentIndex !== undefined
        ? collectDeletionTargetItemIds(watch('itemList') || [], data.parentIndex, data.childIndex)
        : new Set(data.id ? [data.id] : []);
    const references = findItemReferences(targetItemIds, patternMasters, patternDictionaries);

    setItemListDeleteItemData(data);
    setItemListDeleteReferences(references);

    if (hasBlockingReference(references)) {
      setItemDeleteBlockedModalOpen(true);
      return;
    }

    setItemListDeleteModalOpen(true);
  };

  // 項目追加処理（SP時にカード外ボタンから呼ばれる。ItemListManager内部の handleInsertItem と同等）
  const handleAddItem = (): void => {
    const currentList = watch('itemList') || [];
    // 追加する項目はデフォルトでOCR対象のため、OCR対象項目数で上限判定する（#3045）
    const ocrTargetCount = getOcrTargetItemCount(currentList);
    if (ocrTargetCount >= getMaxItemCount()) {
      setErrorText(getItemCountExceededMessage(ocrTargetCount));
      setSnackbarOpen(true);
      return;
    }
    const newItem = {
      id: '0',
      parentValue: '',
      type: 'string',
      children: [],
      is_active: true,
    };
    setValue('itemList', [...currentList, newItem]);
    trigger('itemList');
    setItemListValidated(true);
  };

  const handleItemListDeleteConfirm = (): void => {
    if (!itemListDeleteItemData) return;

    const currentItemList = watch('itemList') || [];

    if (
      itemListDeleteItemData.isChild &&
      itemListDeleteItemData.parentIndex !== undefined &&
      itemListDeleteItemData.childIndex !== undefined
    ) {
      // 子項目の削除
      const parent = currentItemList[itemListDeleteItemData.parentIndex];
      const newChildren = parent.children.filter(
        (_: ItemListChildType, i: number) => i !== itemListDeleteItemData.childIndex,
      );
      const newList = currentItemList.map((item: ItemListType, idx: number) =>
        idx === itemListDeleteItemData.parentIndex
          ? {
              ...item,
              type: newChildren.length === 0 ? 'string' : item.type,
              children: newChildren,
            }
          : item,
      );
      setValue('itemList', newList);
      trigger('itemList');
    } else if (itemListDeleteItemData.parentIndex !== undefined) {
      // 親項目の削除
      const newList = currentItemList.filter(
        (_: ItemListType, idx: number) => idx !== itemListDeleteItemData.parentIndex,
      );
      setValue('itemList', newList);
      trigger('itemList');
    }

    // existingItemIdsからも削除
    setExistingItemIds(prev => prev.filter(existingId => existingId !== itemListDeleteItemData.id));

    setItemListDeleteModalOpen(false);
    setItemListDeleteItemData(null);
    setItemListDeleteReferences([]);
  };

  const handleItemListDeleteClose = (): void => {
    setItemListDeleteModalOpen(false);
    setItemListDeleteItemData(null);
    setItemListDeleteReferences([]);
  };

  // 削除ブロック案内モーダルを閉じる（#3344）
  const handleItemDeleteBlockedClose = (): void => {
    setItemDeleteBlockedModalOpen(false);
    setItemListDeleteItemData(null);
    setItemListDeleteReferences([]);
  };

  // 項目削除ハンドラ（Step3から呼ばれる）

  const isStepCompleted = (step: number): boolean => {
    return completedSteps[step] === true;
  };

  const canNavigateToStep = (step: number): boolean => {
    if (step === 0) return true;
    return step <= activeStep || completedSteps[step - 1] === true;
  };

  // 代理モード時の初回OCRデフォルト担当者を解決する（#2928）
  // 通常モードはアップロード者本人を担当者とするため取得不要
  useEffect(() => {
    if (!isProxyMode || !effectiveCompanyId) {
      setProxyDefaultAssignee(null);
      return;
    }
    let active = true;
    void (async (): Promise<void> => {
      try {
        const rep = await getCompanyRepresentative(effectiveCompanyId);
        if (active) setProxyDefaultAssignee(rep);
      } catch {
        if (active) setProxyDefaultAssignee(null);
      }
    })();
    return (): void => {
      active = false;
    };
  }, [isProxyMode, effectiveCompanyId]);

  useEffect(() => {
    if (!company?.company_id) return;
    if (isProxyMode) {
      setBaseRemainingCount(9999);
      return;
    }
    const companyDocRef = doc(db, 'companies', company.company_id);
    const unsubscribe = onSnapshot(companyDocRef, snapshot => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const currentMonthOcrCost = data.current_month_ocr_cost ?? 0;
        const ocrMonthlyBudget = data.ocr_monthly_budget ?? company.ocr_monthly_budget;
        const creditRate = getCreditRateByPlan(data.plan ?? company.plan);
        setBaseRemainingCount(
          Math.max(0, Math.floor((ocrMonthlyBudget - currentMonthOcrCost) / creditRate)),
        );
        setAllowAiLearning(data.allow_ai_learning ?? true);
      }
    });
    return () => unsubscribe();
  }, [company?.company_id, isProxyMode]);

  // コピー元ID。searchParams オブジェクトそのものを依存に入れると
  // ?step= の書き換え（#3147）でも再読み込みが走ってしまうため、値だけを取り出して依存させる
  const copyPatternId = searchParams.get('copy');

  // 初期ロードは id / uid / コピー元が変わったときだけ実行する。
  // user オブジェクトそのものを依存にすると、AuthContext が新しい参照を配るたびに
  // 読み込みが再実行される（#3259）
  const userId = user?.uid;
  useEffect(() => {
    if (id && userId) {
      setCurrentPatternId(id);
      loadPatternData(id);
    } else if (userId && !id) {
      // コピーパラメータがある場合はコピー元データを読み込み
      if (copyPatternId) {
        loadCopyPatternData(copyPatternId);
      }
    }
  }, [id, userId, copyPatternId]);

  // リダイレクト中フラグ（リダイレクト時にloading=falseにさせないため）
  const isRedirectingRef = useRef<boolean>(false);

  // 読取パターンデータを読み込む
  const loadPatternData = async (patternId: string): Promise<void> => {
    try {
      setLoading(true);
      const patternData = await getPattern(patternId);

      if (!patternData) {
        isRedirectingRef.current = true;
        setErrorText('読取パターンが見つかりません');
        setSnackbarOpen(true);
        navigate('/pattern');
        return;
      }

      // 権限チェック: 運営スタッフ、または同じ会社のメンバー（作成者含む）のみ編集可能
      const isOperator = isOperatorStaff(user?.role);

      // 削除済みパターンのチェック（運営スタッフは閲覧可能）
      if (patternData.status === patternStatus.deleted && !isOperator) {
        isRedirectingRef.current = true;
        setErrorText('この読取パターンは削除されています');
        setSnackbarOpen(true);
        navigate('/pattern');
        return;
      }
      const isOwner = patternData.created_by === user?.uid;
      const isSameCompany = user?.company_id && patternData.company_id === user.company_id;

      // 運営スタッフは全て編集可能（company_idチェック不要）
      // 企業管理者・一般メンバー共通: 作成者または同じ会社であること
      if (!isOperator && !isOwner && !isSameCompany) {
        isRedirectingRef.current = true;
        setErrorText('この読取パターンを編集する権限がありません');
        setSnackbarOpen(true);
        navigate('/pattern');
        return;
      }

      if (patternData) {
        // 削除済みパターンの場合はフラグをセット（運営スタッフのみ閲覧可）
        if (patternData.status === patternStatus.deleted) {
          setIsDeletedPattern(true);
        }

        const fileTypeValue = patternData.file_type || fileProcessingType.pagePerResult;
        reset({
          title: patternData.pattern_name,
          documentName: patternData.document_name || '',
          description: patternData.description || '',
          remarks: patternData.remarks || '',
          fileType: fileTypeValue,
          itemList: patternData.item_list,
          prompt: patternData.prompt || '',
          useCustomPrompt: patternData.use_custom_prompt || false,
          images: [],
          resultData: patternData.result_data || [],
        });
        setIsClassificationTarget(patternData.is_classification_target !== false);
        setSelectedSharedGroupIds(patternData.shared_group_ids ?? []);
        setSelectedSharedUserIds(patternData.shared_user_ids ?? []);

        // useCustomPromptフラグがONの場合は表示
        const shouldShowPrompt = patternData.use_custom_prompt || false;
        setUseCustomPrompt(shouldShowPrompt);
        // use_custom_promptがONの場合は入力欄を表示
        if (shouldShowPrompt) {
          setShowPromptInput(true);
        }

        // Geminiモデルの設定を復元
        if (patternData.gemini_model) {
          setSelectedGeminiModel(toAvailableGeminiModel(patternData.gemini_model));
        }

        // OCRエンジンの設定を復元（#3264）。
        // visionAi は 0 なので、値の有無は null/undefined で判定する
        if (patternData.ocr_engine !== undefined && patternData.ocr_engine !== null) {
          setSelectedOcrEngine(patternData.ocr_engine);
        }

        // 既存ファイル情報をセット（元ファイルがあればそちらを優先、なければ回転済みを表示）
        const filesForStep1 =
          patternData.original_sample_files && patternData.original_sample_files.length > 0
            ? patternData.original_sample_files
            : patternData.sample_files;
        if (filesForStep1 && filesForStep1.length > 0) {
          setExistingFiles(filesForStep1);
        }

        // フォームの再バリデーション
        trigger();

        // 編集時は全ステップを完了済みにする
        setCompletedSteps({ 0: true, 1: true, 2: true });
        // 編集時は項目設定（Step2）から開始する（#3147）
        // Step1から始めると見本ファイルの再アップロードやAI読取に誘導されやすく、
        // 意図せず既存の項目設定が置き換わる事故につながるため
        // URLに ?step= があればそちらを優先し、リロード時に同じステップへ戻れるようにする
        setActiveStep(resolveInitialStep(searchParams.get('step')));
        initialStepRestoredRef.current = true;

        // 既存読取パターンの場合は既存項目のIDリストを保存
        if (patternData.item_list && patternData.item_list.length > 0) {
          // 既存項目のIDリストを保存（親項目と子項目の両方）
          const existingIds: string[] = [];
          patternData.item_list.forEach((item: ItemListType) => {
            existingIds.push(item.id);
            if (item.children && item.children.length > 0) {
              item.children.forEach((child: ItemListChildType) => {
                existingIds.push(child.id);
              });
            }
          });
          setExistingItemIds(existingIds);
        }
      }
    } catch (error) {
      setErrorText('読取パターンの読み込みに失敗しました');
      setSnackbarOpen(true);
    } finally {
      if (!isRedirectingRef.current) {
        setLoading(false);
      }
    }
  };

  // コピー用の読取パターンデータを読み込む（権限チェックなし）
  const loadCopyPatternData = async (patternId: string): Promise<void> => {
    try {
      setLoading(true);
      const patternData = await getPattern(patternId);

      if (!patternData) {
        setErrorText('コピー元の読取パターンが見つかりません');
        setSnackbarOpen(true);
        return;
      }

      // フォームにデータをセット（タイトルにはコピー表記を追加）
      setValue('title', `${patternData.pattern_name}のコピー`);
      setValue('documentName', patternData.document_name || '');
      setValue('description', patternData.description || '');
      setValue('remarks', patternData.remarks || '');
      setValue('fileType', patternData.file_type || fileProcessingType.pagePerResult); // コピー元の値またはデフォルト値
      setValue('itemList', patternData.item_list);
      setValue('prompt', patternData.prompt || '');
      setValue('useCustomPrompt', patternData.use_custom_prompt || false);
      setIsClassificationTarget(patternData.is_classification_target !== false);
      setSelectedSharedGroupIds(patternData.shared_group_ids ?? []);
      setSelectedSharedUserIds(patternData.shared_user_ids ?? []);

      // useCustomPromptフラグがONの場合は表示
      const shouldShowPrompt = patternData.use_custom_prompt || false;
      setUseCustomPrompt(shouldShowPrompt);
      // use_custom_promptがONの場合は入力欄を表示
      if (shouldShowPrompt) {
        setShowPromptInput(true);
      }

      // Geminiモデルの設定を復元
      if (patternData.gemini_model) {
        setSelectedGeminiModel(toAvailableGeminiModel(patternData.gemini_model));
      }

      // OCRエンジンの設定を復元（#3264）。
      // visionAi は 0 なので、値の有無は null/undefined で判定する
      if (patternData.ocr_engine !== undefined && patternData.ocr_engine !== null) {
        setSelectedOcrEngine(patternData.ocr_engine);
      }

      // 既存ファイル情報をセット（元ファイルがあればそちらを優先、なければ回転済みを表示）
      const filesForStep1 =
        patternData.original_sample_files && patternData.original_sample_files.length > 0
          ? patternData.original_sample_files
          : patternData.sample_files;
      if (filesForStep1 && filesForStep1.length > 0) {
        setExistingFiles(filesForStep1);
      }

      // フォームの再バリデーション
      trigger();
    } catch (error) {
      setErrorText('コピー元読取パターンの読み込みに失敗しました');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    trigger,
    reset,
    clearErrors,
  } = useForm<PatternFieldType>({
    resolver: yupResolver(patternRegisterSchema) as Resolver<PatternFieldType>,
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      remarks: null,
      fileType: fileProcessingType.pagePerResult,
      images: [], // ここで必ず空配列をセット
      itemList: [
        {
          id: '1',
          parentValue: '',
          children: [],
        },
      ], // 初期は1つの空項目
      resultData: [], // プレビュー結果データ
      prompt: '', // カスタムプロンプト
      useCustomPrompt: false, // カスタムプロンプト利用フラグ
    },
  });

  const currentUploadedImages = watch('images');
  const currentItemList = watch('itemList');

  // 項目数の監視と警告表示（OCR対象項目数で判定。#3045）
  React.useEffect(() => {
    const ocrTargetCount = getOcrTargetItemCount(currentItemList);

    if (ocrTargetCount > getMaxItemCount()) {
      // 項目上限超過の警告（リアルタイム更新）
      setErrorText(getItemCountExceededMessage(ocrTargetCount));
      setSnackbarOpen(true);
    } else if (snackbarOpen && errorText.includes('AI読取する項目数が上限')) {
      // 上限以下に戻ったらSnackbarを閉じる
      setSnackbarOpen(false);
    }
  }, [currentItemList]);

  // ボタンの非活性判定（初回押下前は有効、押下後にバリデーションエラーがあれば無効化）
  const buttonDisabledFlag = (): boolean => {
    // 初回押下前は有効（ボタンを押せる状態にする）
    if (!isSubmitAttempted) {
      return false;
    }

    // 初回押下後はバリデーション条件をチェック
    const title = watch('title');
    const fileType = watch('fileType');
    return !title || title.trim() === '' || fileType === undefined || fileType === null;
  };

  // ファイルバリデーションメッセージを取得
  const getFileValidationMessage = (): string | undefined => {
    // ボタン押下時のバリデーションエラー
    if (fileUploadError) {
      return fileUploadError;
    }

    if (errors.images?.message) {
      return errors.images.message;
    }

    // 合計ページ数の上限チェック（新規アップロード分のみをカウント）。
    // アップロード時に storeCappedFiles が「既存＋新規 ≤ 上限」を担保するため、ここは実質バックストップ。
    // 既存分を合算しないのは、上限超のレガシーパターンを編集（新規追加なし）する際に保存をブロックしないため（#2961）。
    if (totalPageCount > MAX_TOTAL_PAGES_FOR_REGISTER) {
      return `サンプルファイルの合計枚数が上限を超えています。（${totalPageCount}枚 / 上限${MAX_TOTAL_PAGES_FOR_REGISTER}枚）`;
    }

    return undefined;
  };

  // 編集時の既存サンプルの合計ページ数を数える（PDFはURLからページ数取得・画像は1ページ換算）。
  // download_url が無い場合も storage_path から解決し、多ページPDFを1ページと誤カウントしないようにする。
  const countExistingSamplePages = useCallback(
    async (files: PatternFileType[]): Promise<number> => {
      let total = 0;
      for (const file of files) {
        const isPdf =
          file.file_type === 'application/pdf' || file.file_name.toLowerCase().endsWith('.pdf');
        if (isPdf) {
          const url = await resolvePatternFileUrl(file);
          total += url ? await getPdfPageCountFromUrl(url) : 1;
        } else {
          total += 1;
        }
      }
      return total;
    },
    [],
  );

  // サンプルは合計 PATTERN_SAMPLE_MAX_PAGES ページまで（#2961）。
  // 既存サンプル（編集時）も合算し、超過分は自動で切り落として格納・通知する。
  const storeCappedFiles = useCallback(
    async (newFiles: File[], onChange: (value: File[]) => void): Promise<void> => {
      const existingPages = await countExistingSamplePages(existingFiles);
      const { files: cappedFiles, truncated } = await capFilesToTotalPages(
        newFiles,
        PATTERN_SAMPLE_MAX_PAGES,
        existingPages,
      );
      onChange(cappedFiles);
      setDisplayedFiles(
        cappedFiles.map(file => ({
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          file_path: '',
          file_url: '',
        })),
      );
      if (truncated) {
        // 既存が既に上限で新規を1つも追加できなかった場合は差し替えを促す
        const message =
          existingPages >= PATTERN_SAMPLE_MAX_PAGES && newFiles.length > 0
            ? patternMessages.sampleAlreadyMaxPages
            : patternMessages.sampleTruncatedToMaxPages;
        setInfoText(message);
        setSnackbarOpen(true);
      }
    },
    [countExistingSamplePages, existingFiles, setDisplayedFiles, setInfoText, setSnackbarOpen],
  );

  // アップロードファイル選択時
  const handleFilesSelected = async (
    files: FileList | null,
    onChange: (value: File[]) => void,
  ): Promise<void> => {
    // ファイルアップロードエラーをクリア
    setFileUploadError(undefined);

    if (files) {
      const fileArray = Array.from(files);
      const currentImages = watch('images') || [];
      const fileType = watch('fileType');

      if (fileType === fileProcessingType.pagePerResult) {
        // 1ページ1結果モード: PDFをページ分割してから格納
        const processedFiles: File[] = [];
        for (const file of fileArray) {
          if (isPdfFile(file)) {
            // 元のPDFを保持（fileType切り替え時の復元用）
            setOriginalPdfFiles(prev => new Map(prev).set(file.name, file));
            const splitFiles = await splitPdfIntoPages(file);
            processedFiles.push(...splitFiles);
          } else {
            processedFiles.push(file);
          }
        }

        // サンプルは合計上限ページまで（超過分は切り落とし・通知）
        await storeCappedFiles([...currentImages, ...processedFiles], onChange);
      } else {
        // N:1モード: そのまま格納（合計上限ページまで。超過分は切り落とし・通知）
        await storeCappedFiles([...currentImages, ...fileArray], onChange);
      }

      // バリデーションを実行
      await trigger('images');
    } else {
      onChange([]);
      setDisplayedFiles([]);
      trigger('images');
    }
  };

  // アップロードファイル削除時（同じ元PDFから分割されたファイルをグループ削除）
  const handleFilesDeleted = (index: number): void => {
    const currentImages = watch('images') || [];
    const groupIndices = new Set(getSplitGroupIndices(currentImages, index));
    const updatedImages = currentImages.filter((_, filterIndex) => !groupIndices.has(filterIndex));
    setValue('images', updatedImages);
    trigger('images');

    const newDisplayed = updatedImages.map(file => ({
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
      file_path: '',
      file_url: '',
    }));
    setDisplayedFiles(newDisplayed);
  };

  // 合計ページ数を計算（PDFはページ数分、画像は1ページとしてカウント）
  const watchedImages = watch('images');
  const { totalPageCount, isCalculating: isCalculatingPageCount } =
    useTotalPageCount(watchedImages);

  // ファイル処理方式変更時のページ数再チェック＆PDF分割/復元
  const watchedFileType = watch('fileType');
  const prevFileTypeRef = useRef<number | undefined>(undefined);
  const originalPdfFilesRef = useRef(originalPdfFiles);
  originalPdfFilesRef.current = originalPdfFiles;

  const handleFileTypeChangeCallback = useCallback(
    async (newFileType: number | undefined, currentImages: File[]): Promise<void> => {
      const prevFileType = prevFileTypeRef.current;
      prevFileTypeRef.current = newFileType;

      // 初回レンダリング時はスキップ
      if (prevFileType === undefined) return;
      // 同じ値なら何もしない
      if (prevFileType === newFileType) return;

      if (currentImages.length === 0) return;

      if (newFileType === fileProcessingType.pagePerResult) {
        // N:1 → 1:1に切り替え: PDFをページ分割
        const processedFiles: File[] = [];
        for (const file of currentImages) {
          if (isPdfFile(file)) {
            setOriginalPdfFiles(prev => new Map(prev).set(file.name, file));
            const splitFiles = await splitPdfIntoPages(file);
            processedFiles.push(...splitFiles);
          } else {
            processedFiles.push(file);
          }
        }
        // サンプルは合計上限ページまで（超過分は切り落とし・通知）
        await storeCappedFiles(processedFiles, f => setValue('images', f));
        setFileUploadError(undefined);
      } else if (newFileType === fileProcessingType.singleResult) {
        // 1:1 → N:1に切り替え: 分割済みPDFを元のPDFに復元
        const restoredFiles: File[] = [];
        const processedSiblingGroups = new WeakSet<File>();
        const currentOriginalPdfFiles = originalPdfFilesRef.current;

        for (const file of currentImages) {
          if (isSplitPageFile(file)) {
            const siblings = getSplitSiblings(file);
            if (!processedSiblingGroups.has(siblings[0])) {
              siblings.forEach(s => processedSiblingGroups.add(s));
              const originalFile = currentOriginalPdfFiles.get(
                siblings[0].name.replace(/_\d+(\.[^/.]+)$/, '$1'),
              );
              restoredFiles.push(originalFile ?? file);
            }
          } else {
            restoredFiles.push(file);
          }
        }

        // サンプルは合計上限ページまで（超過分は切り落とし・通知）
        await storeCappedFiles(restoredFiles, f => setValue('images', f));
        setFileUploadError(undefined);
      }
    },
    [setValue, storeCappedFiles],
  );

  useEffect(() => {
    void handleFileTypeChangeCallback(watchedFileType, watchedImages || []);
  }, [watchedFileType, watchedImages, handleFileTypeChangeCallback]);

  // ファイル構成が変わった場合にselectedImageIndexを範囲内に収める
  useEffect(() => {
    const totalFiles = existingFiles.length + (currentUploadedImages?.length || 0);
    if (totalFiles > 0 && selectedImageIndex >= totalFiles) {
      setSelectedImageIndex(totalFiles - 1);
    } else if (totalFiles === 0 && selectedImageIndex !== 0) {
      setSelectedImageIndex(0);
    }
  }, [existingFiles, currentUploadedImages, selectedImageIndex]);

  // 既存ファイル削除のハンドラー
  const handleExistingFileDelete = (index: number): void => {
    const updatedFiles = existingFiles.filter((_, filterIndex) => filterIndex !== index);
    setExistingFiles(updatedFiles);

    // STEP2の画像表示インデックスを調整（範囲外にならないようにする）
    const newTotalFiles = updatedFiles.length + (currentUploadedImages?.length || 0);
    if (selectedImageIndex >= newTotalFiles && newTotalFiles > 0) {
      setSelectedImageIndex(newTotalFiles - 1);
    } else if (newTotalFiles === 0) {
      setSelectedImageIndex(0);
    }
  };

  // 項目読み取り → 自動プレビュー（統合関数）
  const getItemListAndPreview = async (): Promise<void> => {
    setErrorText('');

    const existingCount = existingFiles.length;
    const newCount = currentUploadedImages?.length || 0;
    const totalCount = existingCount + newCount;

    try {
      if (totalCount === 0) {
        throw new Error(patternMessages.noFileSelected);
      }

      // ローディングモーダルを表示（CustomLoadingとSnackbarは表示しない）
      setItemReadingMessage('読取パターンを作成しています...');
      setItemReadingModalOpen(true);
      setItemReadingProgress(0);

      // 進捗を擬似的に更新（実際のAPIは進捗を返さないため）
      const progressInterval = setInterval(() => {
        setItemReadingProgress(prev => {
          if (prev >= 90) {
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 500);

      const itemListResult = await handleOcrItemList(currentUploadedImages || [], existingFiles);

      // 進捗を100%に
      clearInterval(progressInterval);
      setItemReadingProgress(100);

      // 少し待ってからモーダルを閉じる
      await new Promise(resolve => setTimeout(resolve, 300));
      setItemReadingModalOpen(false);

      if (!itemListResult || !itemListResult.items || itemListResult.items.length === 0) {
        throw new Error('項目の読み取りに失敗しました。画像から項目を検出できませんでした。');
      }

      // AIが検出した書類名をフォームに反映（未入力の場合のみ）
      if (itemListResult.documentName && !watch('documentName')) {
        setValue('documentName', itemListResult.documentName);
        setValue('title', itemListResult.documentName);
      }

      const normalized = itemListResult.items.map((item, idx) => {
        const parentId = String(idx + 1);
        const itemType = item.type || 'string';
        return {
          ...item,
          id: parentId,
          type: itemType,
          extractionRule:
            item.extractionRule ?? (itemType === 'date' ? DEFAULT_DATE_EXTRACTION_RULE : ''),
          children: (item.children || []).map((child, cidx) => {
            const childType = child.type || 'string';
            return {
              ...child,
              id: `${parentId}-${cidx + 1}`,
              type: childType,
              displayWidth: child.displayWidth ?? displayWidth.medium,
              extractionRule:
                child.extractionRule ?? (childType === 'date' ? DEFAULT_DATE_EXTRACTION_RULE : ''),
            };
          }),
        };
      });
      setValue('itemList', normalized);
      // 項目設定ステップに移動
      handleNext();
      setLoading(false);
    } catch (error: unknown) {
      let errorText: string;
      if (error instanceof Error) {
        errorText = error.message;
      } else {
        errorText = '処理中にエラーが発生しました。';
      }
      setErrorText(errorText);
      setSnackbarOpen(true);
      setLoading(false);
      setItemReadingModalOpen(false);
    }
  };

  // AIで項目を再読み取り（Step2用）- Step1と同じ動き
  const onReReadItemList = async (): Promise<void> => {
    setErrorText('');
    setItemListValidated(false);
    clearErrors('itemList');

    const existingCount = existingFiles.length;
    const newCount = currentUploadedImages?.length || 0;
    const totalCount = existingCount + newCount;

    try {
      if (totalCount === 0) {
        setErrorText(patternMessages.noFileSelected);
        setSnackbarOpen(true);
        return;
      }

      // ローディングモーダルを表示
      setItemReadingMessage('読取パターンを作成しています...');
      setItemReadingModalOpen(true);
      setItemReadingProgress(0);

      // 進捗を擬似的に更新
      const progressInterval = setInterval(() => {
        setItemReadingProgress(prev => {
          if (prev >= 90) {
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 500);

      const itemListResult = await handleOcrItemList(currentUploadedImages || [], existingFiles);

      // 進捗を100%に
      clearInterval(progressInterval);
      setItemReadingProgress(100);

      // 少し待ってからモーダルを閉じる
      await new Promise(resolve => setTimeout(resolve, 300));
      setItemReadingModalOpen(false);

      if (!itemListResult || !itemListResult.items || itemListResult.items.length === 0) {
        throw new Error('項目の読み取りに失敗しました。画像から項目を検出できませんでした。');
      }

      // AIが検出した書類名をフォームに反映（未入力の場合のみ）
      if (itemListResult.documentName && !watch('documentName')) {
        setValue('documentName', itemListResult.documentName);
        setValue('title', itemListResult.documentName);
      }

      // 新しい項目でitemListを上書き
      const normalized = (itemListResult.items || []).map((item, idx) => {
        const parentId = String(idx + 1);
        return {
          ...item,
          id: parentId,
          type: item.type || 'string',
          children: (item.children || []).map((child, cidx) => ({
            ...child,
            id: `${parentId}-${cidx + 1}`,
            type: child.type || 'string',
            displayWidth: child.displayWidth ?? displayWidth.medium,
          })),
        };
      });
      setValue('itemList', normalized);
      setLoading(false);
    } catch (error: unknown) {
      setItemReadingModalOpen(false);
      let errorText: string;
      if (error instanceof Error) {
        errorText = error.message;
      } else {
        errorText = '項目の読み取り中にエラーが発生しました。';
      }
      setErrorText(errorText);
      setSnackbarOpen(true);
    }
  };

  // Step2の「AIで再読取」押下時のハンドラ（#3147）
  // 編集時は既存項目のIDが振り直されOCR結果との紐付けが失われるため、確認モーダルを挟む
  const handleReReadItemListRequest = async (): Promise<void> => {
    if (isEditMode) {
      setItemOverwriteConfirmTarget('reRead');
      return;
    }
    await onReReadItemList();
  };

  // 項目リストを空1件にリセットして手動入力できる状態にする（#3147）
  const resetItemListToManualInput = (): void => {
    setValue('itemList', [
      {
        id: '0',
        parentValue: '',
        type: 'string',
        children: [],
        is_active: true,
        is_ocr_target: true, // デフォルトでOCR対象（#2968）
      },
    ]);
  };

  // Step2の「リセットして手動入力」押下時のハンドラ（#3147）
  // 既存項目をすべて破棄するため、AI読取と同様に編集時は確認モーダルを挟む
  const handleResetItemListRequest = (): void => {
    if (isEditMode) {
      setItemOverwriteConfirmTarget('reset');
      return;
    }
    resetItemListToManualInput();
  };

  // 項目置き換え確認モーダルで実行を選択したときの処理（#3147）
  const handleItemOverwriteConfirm = async (): Promise<void> => {
    const target = itemOverwriteConfirmTarget;
    setItemOverwriteConfirmTarget(null);
    if (target === 'initial') {
      await getItemListAndPreview();
    } else if (target === 'reRead') {
      await onReReadItemList();
    } else if (target === 'reset') {
      resetItemListToManualInput();
    }
  };

  // 読取パターン保存（実際の保存処理）
  const performSave = async (): Promise<void> => {
    // 新規作成時のパターン数上限チェック（削除済みを除いたアクティブ数で判定）
    if (!currentPatternId && company) {
      const patternLimit = company.pattern_limit ?? planLimits.free.patternLimit;
      const activeCount = await getPatternCountByCompanyId(company.company_id);
      if (activeCount >= patternLimit) {
        setErrorText(
          `読取パターン数が上限（${patternLimit}件）に達しています。新規作成するには既存のパターンを削除してください。`,
        );
        setSnackbarOpen(true);
        return;
      }
    }

    // 項目リストの事前チェック
    const itemList = watch('itemList');

    // 項目数制限チェック（OCR対象項目数で判定。#3045）
    if (isItemCountExceeded(itemList)) {
      const ocrTargetCount = getOcrTargetItemCount(itemList);
      const maxItems = getMaxItemCount();
      setErrorText(
        `AI読取する項目数が上限を超えています（現在：${ocrTargetCount}個/${maxItems}個）。AI読取する項目を${maxItems}個以下に減らしてから保存してください。`,
      );
      setSnackbarOpen(true);
      return;
    }

    // 空の項目名チェック
    if (hasEmptyItems(itemList)) {
      setErrorText('項目名が空の項目があります。すべての項目名を入力してください。');
      setSnackbarOpen(true);
      return;
    }

    // 型の選択チェック
    if (hasInvalidTypes(itemList)) {
      setErrorText('項目の型を選択してください。');
      setSnackbarOpen(true);
      return;
    }

    // 選択肢の設定チェック（#3075）。選択肢型なのに候補が未設定なら保存させない
    const choiceError = findInvalidChoiceItem(itemList);
    if (choiceError) {
      setErrorText(`「${choiceError.itemName}」${choiceError.errorMessage}`);
      setSnackbarOpen(true);
      return;
    }
    // フィールドコード未設定の項目に自動採番してから、フィールドコード（#3175）と
    // 計算フィールド（#3145）の設定を検証する。採番結果は保存時の fillMissingFieldCodes と同じ値
    const filledItemList = fillMissingFieldCodes(itemList);
    setValue('itemList', filledItemList);
    const fieldCodeValidationError = validateFieldCodesInItemList(filledItemList);
    if (fieldCodeValidationError) {
      setErrorText(
        `「${fieldCodeValidationError.itemName}」のフィールドコードに問題があります。${fieldCodeErrorMessages[fieldCodeValidationError.error]}`,
      );
      setSnackbarOpen(true);
      return;
    }
    const calculationError = getCalculationSettingsError(filledItemList);
    if (calculationError !== null) {
      setErrorText(calculationError);
      setSnackbarOpen(true);
      return;
    }

    setLoading(true); // ロード開始
    setErrorText(''); // エラーメッセージをリセット
    setInfoText(''); // infoTextもリセット
    isSaveCompletedRef.current = true; // 保存処理開始（上限チェックのnavigate抑制）

    try {
      if (!user) throw new Error('ログインしてください。');
      // watchからデータを取得
      const watchedData = watch();

      // 編集時は過去に使用された項目IDを履歴から収集し、削除済みIDの再利用を防ぐ（#3242）。
      // 現在の item_list だけを見ると、項目を削除した分だけ最大値が下がり、新規項目に
      // 削除済みIDが再採番される。そのIDをマスタ照合・辞書設定・出力設定が参照していると、
      // 参照が別の項目を指したまま残り、エラーも警告もなく別項目に値が書き込まれる。
      // 新規作成時は履歴が存在しないため収集しない。
      // 履歴取得の失敗で保存自体を止めないよう、失敗時は従来動作（現リストのみで採番）に戻す。
      let usedItemIds: Set<string> | undefined;
      if (currentPatternId) {
        try {
          usedItemIds = await getUsedItemIdsFromHistory(currentPatternId);
        } catch (usedIdsError) {
          Sentry.captureException(usedIdsError);
        }
      }

      // 項目リストのIDを正規化（文字列形式: "1", "2", "2-1", "2-2"...）
      const normalizedItemList = normalizeItemListIds(watchedData.itemList, usedItemIds);

      const newImages = watchedData.images || [];

      // 共有範囲（#2804 Phase2 / 担当者指定 #2963）:
      // グループも担当者も未選択=企業全体 / どちらか1件以上=限定共有（group）
      const derivedShareScope =
        selectedSharedGroupIds.length > 0 || selectedSharedUserIds.length > 0
          ? shareScope.group
          : shareScope.company;

      // 限定共有（group）で保存する際は、操作者本人を必ず共有先に含める（#3126）。
      // 本人を共有先から外すと canAccessByShareScope を満たせず、保存後（新規作成時は
      // サンプルファイル添付の更新時点）に自分の作成物へアクセスできなくなる自己ロックアウトを
      // 防ぐ。企業全体（company）選択時は誤って group 化しないよう、判定後に本人を足す。
      // 代理モードは運営スタッフがルールをバイパスするうえ、企業の共有先に運営UIDを混ぜない
      // ため対象外とする。
      const sharedUserIdsToSave =
        derivedShareScope === shareScope.group && !isProxyMode && user.uid
          ? Array.from(new Set([...selectedSharedUserIds, user.uid]))
          : selectedSharedUserIds;
      let savedPatternId: string;
      if (currentPatternId) {
        // 編集モード：updatePattern を使用
        await updatePattern(
          currentPatternId,
          watchedData.title,
          watchedData.documentName ?? undefined, // 書類の名称（プロンプト内で使用）
          watchedData.description ?? undefined, // 書類の説明を追加
          watchedData.remarks ?? undefined,
          watchedData.fileType,
          normalizedItemList, // 正規化されたitemList
          true, // is_public
          user.uid,
          newImages, // 新規アップロードファイル → original_sample_files
          existingFiles, // 既存のoriginal_sample_files（STEP1表示中のファイル）
          selectedGeminiModel, // Geminiモデル設定
          watchedData.prompt, // カスタムプロンプト
          watchedData.useCustomPrompt, // カスタムプロンプト利用フラグ
          isClassificationTarget, // 自動仕分け対象フラグ
          derivedShareScope, // 共有範囲（#2804 Phase2）
          selectedSharedGroupIds, // グループ限定時の対象（企業全体時は空配列）
          sharedUserIdsToSave, // 担当者限定時の対象（#2963・#3126 本人を含める・企業全体時は空配列）
          selectedOcrEngine, // OCRエンジン設定（#3264）
        );
        savedPatternId = currentPatternId;
      } else {
        // 新規作成モード：postPattern を使用
        const patternId = await postPattern(user.uid, {
          ...watchedData,
          images: newImages, // 新規アップロードファイル → original_sample_files
          itemList: normalizedItemList, // 正規化されたitemList
          existingOriginalFiles: existingFiles.length > 0 ? existingFiles : undefined, // コピー時の元ファイル
          geminiModel: selectedGeminiModel, // Geminiモデル設定
          ocrEngine: selectedOcrEngine, // OCRエンジン設定（#3264）
          companyId: effectiveCompanyId ?? user.company_id ?? undefined, // 会社IDを追加（代理モード時は対象企業ID）
          isProxy: isProxyMode, // 代理操作フラグ
          operatorId: operatorId ?? undefined, // 代理操作時の運営スタッフUID
          isClassificationTarget, // 自動仕分け対象フラグ
          shareScope: derivedShareScope, // 共有範囲（#2804 Phase2）
          sharedGroupIds: selectedSharedGroupIds, // グループ限定時の対象（企業全体時は空配列）
          sharedUserIds: sharedUserIdsToSave, // 担当者限定時の対象（#2963・#3126 本人を含める・企業全体時は空配列）
        });
        if (!patternId) {
          throw new Error('patternコレクションへの登録に失敗しました。');
        }
        // 新規作成後のpatternIdを保持（再保存時に編集モードになるようにする）
        setCurrentPatternId(patternId);
        savedPatternId = patternId;
      }

      // 採番後の itemList をフォームに反映する（#3242）。
      // 新規項目は id: '0' のまま画面に残っており、保存後にリロードせず JSON編集を開くと
      // ID未採番の状態が見えてしまう。保存できた値で上書きし、画面とDBの状態を揃える。
      setValue('itemList', normalizedItemList);

      // 削除された項目を参照しているマスタ照合・辞書変換の設定を解除する（#3344）。
      // updatePattern の後に実行する: syncLabels は「item_id がマップに無ければスキップ」する
      // ため削除項目の参照には触らず、ここでの解除と競合しない。
      //
      // 解除の失敗で保存自体は失敗させない。updatePattern は既に成功しているため、
      // ここで throw すると「項目は消えたが保存エラー」という意味の取れない状態になる。
      // ただし紐付けが残ったことは利用者に知らせる（無言で握りつぶさない）。
      //
      // 編集時のみ実行する（新規作成直後はこのパターンを参照する設定が存在しない）。
      // マスタ・辞書の state 取得が失敗していても解除は動く（cleanup が Firestore を読み直す）。
      if (currentPatternId) {
        try {
          const cleanupResult = await cleanupItemReferences(
            savedPatternId,
            collectItemIds(normalizedItemList),
            effectiveCompanyId ?? user.company_id ?? '',
          );
          // 照合キーの項目が失われたマッピングは照合が成立しない。
          // 項目削除ボタン経由ならブロックされるが、JSON取り込み・AI再読取は
          // itemList を丸ごと差し替えるためここに到達しうる。
          // マッピングは壊さず残すぶん、利用者に設定の作り直しを促す。
          if (cleanupResult.skippedSearchKeyMappingIds.length > 0) {
            Sentry.captureMessage(
              `[PatternAdd] 照合キーに削除済み項目が残っています patternId=${savedPatternId} mappingIds=${cleanupResult.skippedSearchKeyMappingIds.join(',')}`,
            );
            setErrorText(
              '照合キーに使われていた項目が削除されたため、マスタ照合・辞書変換の設定が無効になっています。マスタ照合管理画面で設定を見直してください。',
            );
            setSnackbarOpen(true);
          }
          // 解除後の状態を保持し、続けて項目を削除する場合の参照判定を最新にする。
          // 企業設定が OFF なら 0 件が返るだけなのでフラグで分岐しない。
          if (cleanupResult.updatedCount > 0 && effectiveCompanyId) {
            setPatternMasters(await getMastersByPatternId(effectiveCompanyId, savedPatternId));
            setPatternDictionaries(
              await getDictionariesByPatternId(effectiveCompanyId, savedPatternId),
            );
          }
        } catch (cleanupError) {
          Sentry.captureException(cleanupError);
          setErrorText('マスタ照合設定の更新に失敗しました。');
          setSnackbarOpen(true);
        }
      }

      // 編集履歴を保存（前回履歴から差分があれば1件追加）
      try {
        const latestHistory = await getLatestPatternHistory(savedPatternId);
        const promptForHistory = watchedData.prompt ?? '';
        const useCustomPromptForHistory = watchedData.useCustomPrompt ?? false;
        const geminiModelForHistory = selectedGeminiModel ?? null;
        const ocrEngineForHistory = selectedOcrEngine ?? null;
        const hasDiff = hasPatternHistoryDiff(
          latestHistory ? toPatternHistorySnapshot(latestHistory) : null,
          {
            prompt: promptForHistory,
            useCustomPrompt: useCustomPromptForHistory,
            geminiModel: geminiModelForHistory,
            ocrEngine: ocrEngineForHistory,
            itemList: normalizedItemList,
          },
        );
        if (hasDiff) {
          await createPatternHistory({
            patternId: savedPatternId,
            prompt: promptForHistory,
            useCustomPrompt: useCustomPromptForHistory,
            itemList: normalizedItemList,
            geminiModel: geminiModelForHistory,
            ocrEngine: ocrEngineForHistory,
            changedBy: user.uid,
            changedByName: user.name ?? '',
          });
        }
      } catch (historyError) {
        // 履歴保存失敗はメイン保存フローを止めない（既存の updatePattern と同方針）
        Sentry.captureException(historyError);
      }

      // 指定フォーマット出力（#3070）の未保存変更をここで確定する。
      // 画面では書き込まず、パターン保存に合わせて反映することで他の設定と挙動を揃える
      let templateErrorText = '';
      if (hasPendingExportTemplateChanges(pendingExportTemplates)) {
        try {
          await commitExportTemplates({
            pending: pendingExportTemplates,
            patternId: savedPatternId,
            // 代理ログイン中は対象企業、そうでなければ自社。運営が直接操作している
            // ときも自社（運営企業）のIDが入るようにする（空文字だと取得側で一致しない）
            companyId: effectiveCompanyId || user.company_id || '',
            userId: user.uid,
            userName: user.name || '',
          });
          setPendingExportTemplates({ added: [], removedIds: [], replaced: [], updatedSheets: [] });
        } catch (templateError) {
          // パターン自体は保存済みのため、フォーマットの失敗だけを知らせて続行する
          Sentry.captureException(templateError);
          templateErrorText =
            templateError instanceof Error
              ? templateError.message
              : '指定フォーマットの保存に失敗しました。';
        }
      }

      // 成功モーダルを表示（フォーマットの失敗があればその内容を残す）
      setErrorText(templateErrorText);
      if (templateErrorText) setSnackbarOpen(true);
      setPatternCompleteModalOpen(true);

      // サイドバーの読取パターンリストを更新するためのイベントを発火
      window.dispatchEvent(new Event('pattern-list-updated'));

      // 新規アップロードファイルをモーダル表示用に保持
      const newlyUploadedFiles = watchedData.images || [];
      if (newlyUploadedFiles.length > 0) {
        setUploadedFilesForOcr(newlyUploadedFiles);
      }
    } catch (error: unknown) {
      isSaveCompletedRef.current = false; // エラー時はフラグをリセット
      let errorText: string;
      if (error instanceof Error) {
        // Error インスタンスの場合
        errorText = error.message;
      } else {
        // その他のエラーの場合
        errorText = '予期せぬエラーが発生しました。';
      }
      setErrorText(errorText);
      setSnackbarOpen(true);
    } finally {
      setLoading(false); // ロード終了
    }
  };

  // 読取パターン保存
  const onSave = async (): Promise<void> => {
    // 保存処理を実行
    await performSave();
  };

  // バリデーションエラー時の処理

  const onError = (errors: FieldErrors<PatternFieldType>): void => {
    const errorMessages: string[] = [];

    // 項目数制限チェック（OCR対象項目数で判定。#3045）
    const currentItemList = watch('itemList');
    if (isItemCountExceeded(currentItemList)) {
      const ocrTargetCount = getOcrTargetItemCount(currentItemList);
      errorMessages.push(
        `AI読取する項目数が制限を超えています（現在：${ocrTargetCount}個）。AI読取する項目を${getMaxItemCount()}個以下に減らしてから実行してください。`,
      );
    }

    // 選択肢の設定チェック（#3075）。候補が未設定のままOCRを実行させない
    const choiceError = findInvalidChoiceItem(currentItemList);
    if (choiceError) {
      errorMessages.push(`「${choiceError.itemName}」${choiceError.errorMessage}`);
    }

    if (errors.title) {
      errorMessages.push(`タイトルは${errors.title.message}。`);
    }
    if (errors.images) {
      errorMessages.push(`ファイルは${errors.images.message}。`);
    }
    if (errors.itemList) {
      // itemListの配列レベルのエラーメッセージを表示（例：項目数制限）
      if (errors.itemList.message) {
        errorMessages.push(errors.itemList.message);
      } else if (Array.isArray(errors.itemList)) {
        // 各項目のエラーを詳細に表示
        errors.itemList.forEach((itemError, index) => {
          if (itemError?.parentValue?.message) {
            errorMessages.push(`項目${index + 1}: ${itemError.parentValue.message}`);
          }
          if (itemError?.children && Array.isArray(itemError.children)) {
            (itemError.children as Array<{ childValue?: { message?: string } }>).forEach(
              (childError, childIndex) => {
                if (childError?.childValue?.message) {
                  errorMessages.push(
                    `項目${index + 1}-${childIndex + 1}: ${childError.childValue.message}`,
                  );
                }
              },
            );
          }
        });
        if (errorMessages.length === 0) {
          errorMessages.push(`取得したい項目リストでエラーが発生しています。`);
        }
      } else {
        errorMessages.push(`取得したい項目リストでエラーが発生しています。`);
      }
    }
    if (errorMessages.length > 0) {
      setErrorText(errorMessages.join('\n'));
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    const loadPatternAndResults = async (): Promise<void> => {
      if (currentPatternId) {
        try {
          const patternDataRes = await getPattern(currentPatternId);
          if (!patternDataRes) {
            throw new Error('読取パターンデータが見つかりません。');
          }

          // フォームの他のフィールドをリセット (images フィールドはクリア)
          // typeフィールドがない場合はデフォルト値を設定
          const itemListWithTypes = (patternDataRes.item_list || []).map((item: ItemListType) => ({
            ...item,
            type: item.type || 'string',
            children: (item.children || []).map((child: ItemListChildType) => ({
              ...child,
              type: child.type || 'string',
              displayWidth: child.displayWidth ?? displayWidth.medium,
            })),
          }));

          reset({
            title: patternDataRes.pattern_name || '',
            documentName: patternDataRes.document_name || '',
            description: patternDataRes.description || '',
            remarks: patternDataRes.remarks || null,
            fileType: patternDataRes.file_type || fileProcessingType.pagePerResult, // 保存された値またはデフォルト値
            itemList: itemListWithTypes,
            images: [], // フォームのimagesはクリア
            resultData: patternDataRes.result_data || [], // 既存の結果データを復元
            prompt: patternDataRes.prompt || '',
            useCustomPrompt: patternDataRes.use_custom_prompt || false,
          });

          setDisplayedFiles([]); // ファイルリストをクリア

          // 既存ファイル（PDF含む）をSTEP1に表示するために復元（元ファイル優先）
          const filesForStep1 =
            patternDataRes.original_sample_files && patternDataRes.original_sample_files.length > 0
              ? patternDataRes.original_sample_files
              : patternDataRes.sample_files;
          if (filesForStep1 && filesForStep1.length > 0) {
            setExistingFiles(filesForStep1);
          }
        } catch (error) {
          let errorText: string;
          if (error instanceof Error) {
            errorText = error.message;
          } else {
            errorText = '予期せぬエラーが発生しました。';
          }
          setErrorText(errorText);
          setSnackbarOpen(true);
          return;
        }
      }
    };

    void loadPatternAndResults();
  }, [currentPatternId, reset]);

  // 項目削除時の参照判定に使うマスタ照合・辞書変換の設定を取得する（#3344）。
  // 編集時のみ取得する（新規作成時はこのパターンを参照する設定が存在しないため）。
  // 取得失敗時は警告なしで削除を許可する（従来動作へのフォールバック）。
  // ここでブロックすると、マスタ照合を使っていない利用者まで項目削除が操作不能になるため。
  useEffect(() => {
    const loadItemReferenceSources = async (): Promise<void> => {
      if (!currentPatternId || !effectiveCompanyId) return;

      // 企業設定が OFF のときは明示的に空へ戻す。編集中に設定が OFF になった場合や
      // 代理モードで対象企業が切り替わった場合に、古い参照で判定しないようにする。
      if (company?.enable_master) {
        try {
          setPatternMasters(await getMastersByPatternId(effectiveCompanyId, currentPatternId));
        } catch (masterError) {
          Sentry.captureException(masterError);
        }
      } else {
        setPatternMasters([]);
      }

      if (company?.enable_dictionary) {
        try {
          setPatternDictionaries(
            await getDictionariesByPatternId(effectiveCompanyId, currentPatternId),
          );
        } catch (dictionaryError) {
          Sentry.captureException(dictionaryError);
        }
      } else {
        setPatternDictionaries([]);
      }
    };

    void loadItemReferenceSources();
  }, [currentPatternId, effectiveCompanyId, company?.enable_master, company?.enable_dictionary]);

  const formatFileSize = (size: number): string => {
    if (size >= 1_000_000_000) return `${(size / 1_000_000_000).toFixed(2)} GB`;
    if (size >= 1_000_000) return `${(size / 1_000_000).toFixed(2)} MB`;
    if (size >= 1_000) return `${(size / 1_000).toFixed(2)} KB`;
    return `${size} B`;
  };

  // 現在表示中の画像を取得する関数（PDFは変換後の全ページを返す）
  const getCurrentDisplayImages = (): {
    dataUrl: string;
    width: number;
    height: number;
    page: number;
  }[] => {
    const allFiles = [...existingFiles, ...(currentUploadedImages || [])];

    if (allFiles.length === 0 || selectedImageIndex >= allFiles.length) return [];

    const file = allFiles[selectedImageIndex];
    const fileName = 'file_name' in file ? file.file_name : file.name;

    // PDFの場合は変換された画像があるかチェック
    if (getCurrentFileType() === 'pdf') {
      const convertedImages = convertedPdfImages.get(fileName);
      if (convertedImages && convertedImages.length > 0) {
        return convertedImages;
      }
      // 変換されていない場合は変換を実行
      convertPdfToImageForDisplay(file, fileName);
      return []; // 変換中は何も表示しない
    }

    if ('download_url' in file && file.download_url) {
      // 既存ファイルの場合
      return [{ dataUrl: file.download_url, width: 0, height: 0, page: 1 }];
    } else if ('name' in file) {
      // 新規アップロードファイルの場合
      return [{ dataUrl: URL.createObjectURL(file), width: 0, height: 0, page: 1 }];
    }
    return [];
  };

  // PDFを画像に変換して表示用のstateに保存する関数（全ページ対応）
  // 変換はレンダー中に呼ばれるため、進行中・失敗済みのファイルを ref で覚えて再実行を防ぐ。
  // ガードがないと、変換完了までの再レンダーごとに新しい変換が走り、
  // メインスレッドを塞いで画面が固まる（#3259）。
  const convertPdfToImageForDisplay = async (
    file: PatternFileType | File,
    fileName: string,
  ): Promise<void> => {
    if (convertingPdfRef.current.has(fileName)) return;
    if (failedPdfRef.current.has(fileName)) return;
    convertingPdfRef.current.add(fileName);

    try {
      if (file instanceof File) {
        // 新規アップロードファイルの場合
        const convertedImages = await convertPdfToImages(file);
        if (convertedImages.length > 0) {
          setConvertedPdfImages(prev => new Map(prev.set(fileName, convertedImages)));
        }
      } else if (file.download_url) {
        // 既存ファイルの場合 - ダウンロードしてから変換
        const response = await fetch(file.download_url);
        const blob = await response.blob();
        const fileObject = new File([blob], fileName, { type: 'application/pdf' });
        const convertedImages = await convertPdfToImages(fileObject);
        if (convertedImages.length > 0) {
          setConvertedPdfImages(prev => new Map(prev.set(fileName, convertedImages)));
        }
      }
    } catch (error) {
      // 変換に失敗したファイルは記録し、再レンダーのたびに再試行しないようにする。
      // 記録しないと「PDF変換中...」の表示のまま無限にリトライを続ける
      failedPdfRef.current.add(fileName);
      setPdfConvertFailedFiles(prev => new Set(prev).add(fileName));
    } finally {
      convertingPdfRef.current.delete(fileName);
    }
  };

  // ファイル名を取得する関数
  const getCurrentFileName = (): string => {
    const allFiles = [...existingFiles, ...(currentUploadedImages || [])];
    if (allFiles.length === 0 || selectedImageIndex >= allFiles.length) return '';

    const file = allFiles[selectedImageIndex];
    return 'file_name' in file ? file.file_name : file.name;
  };

  // ファイルタイプを判定する関数
  const getCurrentFileType = (): 'pdf' | 'image' | 'unknown' => {
    const fileName = getCurrentFileName().toLowerCase();
    if (fileName.endsWith('.pdf')) return 'pdf';
    if (fileName.endsWith('.png') || fileName.endsWith('.jpg') || fileName.endsWith('.jpeg'))
      return 'image';
    return 'unknown';
  };

  // 新規作成時のパターン数上限チェック（URLに直接アクセスした場合のガード）
  // 新規作成時のパターン数上限チェック（URLに直接アクセスした場合のガード）
  // Firestoreから削除済みを除いたアクティブなパターン数を直接取得して判定する
  const patternLimitCheckedRef = React.useRef(false);
  useEffect(() => {
    if (isLoading || !company || id || isSaveCompletedRef.current) return;
    if (patternLimitCheckedRef.current) return;
    patternLimitCheckedRef.current = true;

    const checkLimit = async (): Promise<void> => {
      const patternLimit = company.pattern_limit ?? planLimits.free.patternLimit;
      const activeCount = await getPatternCountByCompanyId(company.company_id);
      if (activeCount >= patternLimit) {
        navigate('/pattern');
      }
    };
    checkLimit();
  }, [isLoading, id, navigate, company]);

  // プレビュー画像はレンダーごとに1回だけ取得する。
  // getCurrentDisplayImages() は未変換のPDFに対して変換を起動するため、
  // JSX内で複数回呼ぶとその回数だけ変換が多重に走る
  const displayImages = getCurrentDisplayImages();

  // 編集モードの初期ロード中・リダイレクト中はローディングのみ表示
  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          { backgroundColor: gray5, marginTop: isProxyMode ? `${PROXY_BANNER_HEIGHT}px` : 0 },
          (activeStep === 0 || activeStep === 2) && {
            '@media screen and (max-width:960px)': {
              paddingBottom: '40px',
            },
          },
          activeStep === 1 && {
            '@media screen and (max-width:960px)': {
              paddingBottom: '24px',
            },
          },
          activeStep === 2 && { minHeight: '100vh', paddingBottom: '70px' },
          id && loading ? { visibility: 'hidden' } : {},
        ]}
      >
        <Box
          sx={[
            layoutStyles.header,
            {
              justifyContent: 'space-between',
              height: 'auto',
              minHeight: '56px',
              padding: '12px 32px',
              '@media screen and (max-width:960px)': {
                minHeight: '48px',
                padding: '12px 16px',
              },
            },
          ]}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={styles.headerTitle}>{getPageTitle()}</Typography>
            {isDeletedPattern && <DeletedBadge />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box
              component="button"
              onClick={(): void => setTutorialModalOpen(true)}
              sx={styles.headerLink}
            >
              <ReplayRoundedIcon sx={{ fontSize: '16px' }} />
              チュートリアルをみる
            </Box>
            <Box sx={{ width: '1px', height: '16px', backgroundColor: gray40 }} />
            <Box
              component="a"
              href={TUTORIAL_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={styles.headerLink}
            >
              <PlayArrowRoundedIcon sx={{ fontSize: '20px' }} />
              説明動画をみる
            </Box>
          </Box>
        </Box>

        {/* 背景エリア */}
        <Box
          sx={
            activeStep === 0
              ? styles.contentAreaBackground
              : activeStep === 2
                ? styles.contentAreaBackgroundCompact
                : styles.contentAreaBackgroundWide
          }
        >
          {/* Stepper - カードの外に配置 */}
          <Box sx={styles.stepperContainer}>
            <Stepper nonLinear activeStep={activeStep} sx={styles.stepper}>
              {STEPS.map((label, index) => (
                <Step key={label} completed={isStepCompleted(index)}>
                  <StepButton
                    color="inherit"
                    onClick={handleStep(index)}
                    disabled={!canNavigateToStep(index)}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Step 1: ファイルアップロードセクション */}
          {activeStep === 0 && (
            <Box sx={[styles.mainContainer, { marginBottom: '48px' }]}>
              <Box sx={styles.deliveryMainInfo}>
                <Typography sx={styles.sectionTitleSimple}>読取ファイルを選ぶ</Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: gray100,
                    textAlign: 'left',
                    lineHeight: 1.6,
                    marginBottom: '16px',
                  }}
                >
                  読取パターンを作るための見本ファイルを選んでください。
                </Typography>
                {/* 編集時はAI読取で既存の項目設定が置き換わるため警告する（#3147）
                    AI読取の導線がないユーザーには関係しないため表示しない */}
                {isEditMode && canUseAiItemRead && (
                  <MuiAlert
                    severity="warning"
                    icon={<ErrorOutlineIcon />}
                    sx={{
                      marginBottom: '8px',
                      color: yellow50,
                      backgroundColor: yellow_overlay10,
                      textAlign: 'left',
                      alignItems: 'flex-start',
                      '& .MuiAlert-icon': {
                        color: yellow50,
                      },
                      '& .MuiAlert-message': {
                        textAlign: 'left',
                      },
                    }}
                  >
                    {patternMessages.aiReadOverwriteWarningOnEdit}
                  </MuiAlert>
                )}
                {getFileValidationMessage() && (
                  <MuiAlert
                    severity="warning"
                    icon={<ErrorOutlineIcon />}
                    sx={{
                      marginBottom: '8px',
                      color: yellow50,
                      backgroundColor: yellow_overlay10,
                      '& .MuiAlert-icon': {
                        color: yellow50,
                      },
                    }}
                  >
                    {getFileValidationMessage()}
                  </MuiAlert>
                )}
                <Controller
                  render={({ field }): JSX.Element => (
                    <PatternFileUploader
                      labelText=""
                      requiredText=""
                      helperText={undefined}
                      height="140px"
                      onFilesSelected={(files: FileList | null): void => {
                        void handleFilesSelected(files, field.onChange);
                      }}
                    />
                  )}
                  control={control}
                  name="images"
                />

                <Typography
                  sx={{
                    ...regularSm,
                    color: grayBlue50,
                    textAlign: 'left',
                    margin: '10px 0 10px 0',
                  }}
                >
                  ・最大{MAX_TOTAL_PAGES_FOR_REGISTER}
                  枚までです（PDFはページ数分、画像は1枚としてカウント）。
                  <br />
                  ・各ファイルのサイズは10MBまでです。
                </Typography>

                {/* 統合されたファイル一覧表示テーブル */}
                {(currentUploadedImages && currentUploadedImages.length > 0) ||
                displayedFiles.length > 0 ||
                existingFiles.length > 0 ? (
                  <>
                    <TableContainer sx={styles.tableContainer}>
                      <Table>
                        <TableBody>
                          {/* 既存ファイルを最初に表示 */}
                          {existingFiles.map((file: PatternFileType, index: number) => (
                            <TableRow key={`existing-file-${index}`} sx={styles.TableRow}>
                              <TableCell sx={styles.TableCell}>
                                <Button
                                  sx={{
                                    textAlign: 'left',
                                    padding: 0,
                                    minWidth: 'auto',
                                    color: '#1976d2',
                                    textTransform: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                      backgroundColor: 'transparent',
                                    },
                                  }}
                                  onClick={(): void => {
                                    setSelectedImageIndex(index);
                                    if (file.download_url) {
                                      window.open(file.download_url, '_blank');
                                    }
                                  }}
                                >
                                  {file.file_name}
                                </Button>
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...styles.TableCell,
                                  color: gray80,
                                  textAlign: 'right',
                                  paddingRight: '4px',
                                  width: '80px',
                                  minWidth: '80px',
                                  maxWidth: '80px',
                                }}
                              >
                                {formatFileSize(file.file_size)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...styles.TableCell,
                                  textAlign: 'end',
                                  width: '32px',
                                  minWidth: '32px',
                                  maxWidth: '32px',
                                  padding: '8px 8px 8px 0',
                                }}
                              >
                                <Button
                                  sx={styles.cancelButton}
                                  onClick={(): void => handleExistingFileDelete(index)}
                                >
                                  <CancelIcon />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* 新規アップロードファイルを既存ファイルの後に表示 */}
                          {(currentUploadedImages && currentUploadedImages.length > 0
                            ? currentUploadedImages
                            : displayedFiles
                          ).map((file: File | InputFileType, index: number) => {
                            const actualIndex = existingFiles.length + index;
                            const isEditing =
                              currentUploadedImages && currentUploadedImages.length > 0;
                            const showDelete =
                              isEditing &&
                              isFirstInSplitGroup(currentUploadedImages as File[], index);
                            return (
                              <TableRow key={`new-file-${index}`} sx={styles.TableRow}>
                                <TableCell sx={styles.TableCell}>
                                  <Button
                                    sx={{
                                      textAlign: 'left',
                                      padding: 0,
                                      minWidth: 'auto',
                                      color: '#1976d2',
                                      textTransform: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                        backgroundColor: 'transparent',
                                      },
                                    }}
                                    onClick={(): void => {
                                      setSelectedImageIndex(actualIndex);
                                      if ('name' in file) {
                                        const url = URL.createObjectURL(file as File);
                                        window.open(url, '_blank');
                                      } else if (file.file_url) {
                                        window.open(file.file_url, '_blank');
                                      }
                                    }}
                                  >
                                    {/* File型ならname、InputFileType型ならfile_name */}
                                    {'name' in file ? file.name : file.file_name}
                                  </Button>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...styles.TableCell,
                                    color: gray80,
                                    textAlign: 'right',
                                    paddingRight: '4px',
                                    width: '80px',
                                    minWidth: '80px',
                                    maxWidth: '80px',
                                  }}
                                >
                                  {/* File型ならsize、InputFileType型ならfile_size */}
                                  {formatFileSize('size' in file ? file.size : file.file_size)}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...styles.TableCell,
                                    textAlign: 'end',
                                    width: '32px',
                                    minWidth: '32px',
                                    maxWidth: '32px',
                                    padding: '8px 8px 8px 0',
                                  }}
                                >
                                  {/* グループ最後の行にのみキャンセルボタンを表示 */}
                                  {showDelete ? (
                                    <Button
                                      sx={styles.cancelButton}
                                      onClick={(): void => handleFilesDeleted(index)}
                                    >
                                      <CancelIcon />
                                    </Button>
                                  ) : (
                                    <Box sx={{ width: '24px', height: '24px' }} />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : null}

                {/* 出力方式選択 */}
                <Controller
                  render={({ field }): JSX.Element => (
                    <OutputTypeSelector
                      value={field.value}
                      onChange={(value): void => field.onChange(value)}
                    />
                  )}
                  control={control}
                  name="fileType"
                />

                {/* モデル設定（運営スタッフのみ表示）。
                    出力方式までの基本設定と区切り、処理順（OCR → Gemini）に合わせて並べる */}
                {isOperatorStaff(user?.role) && (
                  <>
                    <Divider sx={{ borderColor: gray40, margin: '16px 0' }} />
                    <Typography
                      sx={{
                        color: grayBlue50,
                        fontSize: '16px',
                        fontWeight: 700,
                        mb: '16px',
                        textAlign: 'left',
                      }}
                    >
                      モデル設定
                    </Typography>
                    <Box
                      sx={{
                        marginBottom: '24px',
                        maxWidth: '400px',
                        // 各 CustomSelectField のラベルと必須マークの見た目をそろえる
                        '& > div > div:first-of-type': {
                          '& > span:first-of-type': {
                            color: grayBlue50,
                            fontSize: '16px',
                            fontWeight: 700,
                          },
                          '& > span:last-of-type': {
                            color: orange50,
                            fontSize: '14px',
                            fontWeight: 400,
                          },
                        },
                      }}
                    >
                      {/* OCRエンジン選択（#3264）。
                          帳票によって Vision AI と Document Intelligence のどちらが読めるかが
                          変わるため、パターン単位で選べるようにしている。
                          未設定（既存パターン）は Vision AI として扱う。

                          選択肢は「どういう帳票か」を主にしつつ、エンジン名も添える。
                          この画面は運営しか見ないため、検証結果と突き合わせられるようにする。
                          ユーザー向けのアップロード画面ではエンジン名を出さない（ocrEngineChoice）。
                          ユーザーへの課金は変わらない（原価のみ変動）ので費用は出さない */}
                      <CustomSelectField
                        value={selectedOcrEngine}
                        onChange={(event): void => {
                          setSelectedOcrEngine(Number(event.target.value));
                        }}
                        labelText="OCRエンジン"
                        requiredText="必須"
                        list={ocrEngineChoiceForOperator.map(c => ({
                          option: c.label,
                          value: c.value,
                        }))}
                      />

                      <Box sx={{ marginTop: '24px' }}>
                        <CustomSelectField
                          value={selectedGeminiModel}
                          onChange={(event): void => {
                            setSelectedGeminiModel(Number(event.target.value));
                          }}
                          labelText="Geminiモデル"
                          requiredText="必須"
                          list={[
                            {
                              // 既定モデル。#3375 で 2.5 Flash から 3.5 Flash-Lite へ変更した。
                              // 2.5 Flash で保存された既存パターンも toAvailableGeminiModel で
                              // ここに寄せられるため、選択肢は GEMINI_MODEL_DEFAULT で持つ
                              option: `Gemini 3.5 Flash-Lite（デフォルト・低コスト）- ${getOcrCreditRateWithModel(company?.plan ?? 0, GEMINI_MODEL_DEFAULT)}円/枚`,
                              value: GEMINI_MODEL_DEFAULT,
                            },
                            {
                              option: `Gemini 2.5 Pro（高精度）- ${getOcrCreditRateWithModel(company?.plan ?? 0, geminiModel.pro_2_5)}円/枚`,
                              value: geminiModel.pro_2_5,
                            },
                          ]}
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* 固定フッター：項目をAIで読み取る・次へボタン（Step 0） */}
          {activeStep === 0 && (
            <Box sx={[styles.fixedFooter, { left: sideBarCollapsed ? '80px' : '240px' }]}>
              {/* カスタムプロンプトONの一般ユーザーにはAI読取ボタンを非表示 */}
              {canUseAiItemRead && (
                <Button
                  // 編集時はプライマリから外し、誤って押しにくくする（#3147）
                  sx={isEditMode ? styles.secondaryFooterButton : styles.primaryFooterButton}
                  disabled={!!getFileValidationMessage() || isCalculatingPageCount}
                  onClick={async (): Promise<void> => {
                    // ファイルがアップロードされているかチェック
                    const images = watch('images') || [];
                    if (images.length === 0 && existingFiles.length === 0) {
                      setFileUploadError(patternMessages.noFileSelected);
                      return;
                    }
                    setFileUploadError(undefined);

                    // 編集時は既存の項目設定が置き換わるため確認を挟む（#3147）
                    if (isEditMode) {
                      setItemOverwriteConfirmTarget('initial');
                      return;
                    }

                    await getItemListAndPreview();
                  }}
                >
                  <Typography
                    sx={
                      isEditMode
                        ? { fontSize: '14px', fontWeight: 500, color: gray80 }
                        : { fontSize: '16px', fontWeight: 700, color: white }
                    }
                  >
                    項目をAIで読み取る
                  </Typography>
                </Button>
              )}
              {/* 次へボタンの表示条件:
                  - 運営スタッフ: ファイル・AI読み取り不要で常に表示（手動入力/JSON取り込みで項目設定可能）
                  - それ以外: 項目読み取り済み（parentValueが入力されている項目がある）の場合のみ表示 */}
              {(isOperatorStaff(user?.role) ||
                watch('itemList')?.some(
                  item => item.parentValue && item.parentValue.trim() !== '',
                )) && (
                <Button
                  // 編集時は「次へ」を主導線にして、AI読取より優先して押されるようにする（#3147）
                  sx={isEditMode ? styles.primaryFooterButton : styles.secondaryFooterButton}
                  onClick={(): void => {
                    // 運営が項目未読み取りのまま進む場合、手動入力できるよう空項目を1件用意する
                    const currentItemList = watch('itemList') || [];
                    const hasAnyItem = currentItemList.some(
                      item => item.parentValue && item.parentValue.trim() !== '',
                    );
                    if (!hasAnyItem) {
                      setValue('itemList', [
                        {
                          id: '0',
                          parentValue: '',
                          type: 'string',
                          children: [],
                          is_active: true,
                        },
                      ]);
                    }
                    setCompletedSteps(prev => ({ ...prev, 0: true }));
                    setItemListValidated(false);
                    clearErrors('itemList');
                    setActiveStep(1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <Typography
                    sx={
                      isEditMode
                        ? { fontSize: '16px', fontWeight: 700, color: white }
                        : { fontSize: '14px', fontWeight: 500, color: gray80 }
                    }
                  >
                    次へ
                  </Typography>
                </Button>
              )}
            </Box>
          )}

          {activeStep >= 1 && (
            <Box sx={styles.mainContainerWide}>
              {/* Step 2: 項目設定セクション */}
              {activeStep === 1 && (
                <Box
                  sx={[
                    styles.deliveryMainInfoWide,
                    {
                      paddingBottom: '48px',
                      '@media (max-width: 960px)': {
                        paddingBottom: '16px',
                      },
                    },
                  ]}
                >
                  {/* カスタムプロンプト通知（ユーザー向け） */}
                  {!isOperatorStaff(user?.role) && useCustomPrompt && (
                    <Box
                      sx={{
                        marginTop: '54px',
                        marginBottom: '32px',
                        marginLeft: '32px',
                        marginRight: '32px',
                        '@media (max-width: 1200px)': {
                          marginLeft: '16px',
                          marginRight: '16px',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '8px',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          sx={{
                            ...styles.subSectionTitle,
                            margin: 0,
                            paddingLeft: 0,
                            '&::before': { display: 'none' },
                          }}
                        >
                          カスタムプロンプト
                        </Typography>
                        <Switch checked={true} disabled />
                        <Typography sx={{ ...boldSm, color: gray80 }}>
                          {useCustomPrompt ? 'ON' : 'OFF'}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography
                          sx={{
                            ...regularSm,
                            color: gray80,
                            marginBottom: '8px',
                            textAlign: 'left',
                          }}
                        >
                          事務局によって項目リストとプロンプトが直接指定されています。
                        </Typography>
                        <Typography sx={{ ...regularSm, color: gray80, textAlign: 'left' }}>
                          項目リストやプロンプトを変更したい場合は、事務局にご相談ください。
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box ref={itemSettingContainerRef} sx={styles.itemSettingContainer}>
                    {/* 左側：画像表示エリア */}
                    <Box sx={styles.imageDisplayColumn}>
                      <Box sx={styles.imageDisplayArea}>
                        {/* ファイル名と左右矢印のナビゲーション */}
                        <Box sx={styles.imageNavigation}>
                          {selectedImageIndex > 0 ? (
                            <Button
                              sx={styles.navigationButton}
                              onClick={(): void => setSelectedImageIndex(selectedImageIndex - 1)}
                            >
                              <ArrowBackIosIcon sx={{ fontSize: '16px' }} />
                              <Typography sx={styles.navigationButtonText}>前のファイル</Typography>
                            </Button>
                          ) : (
                            <Box sx={styles.navigationPlaceholder} />
                          )}
                          <Typography sx={styles.imageNavigationText}>
                            {getCurrentFileName() || 'ファイルが選択されていません'}
                            {((): string => {
                              const allFiles = [...existingFiles, ...(currentUploadedImages || [])];
                              return allFiles.length > 1
                                ? ` (${selectedImageIndex + 1}/${allFiles.length})`
                                : '';
                            })()}
                          </Typography>
                          {((): boolean => {
                            const allFiles = [...existingFiles, ...(currentUploadedImages || [])];
                            return selectedImageIndex < allFiles.length - 1;
                          })() ? (
                            <Button
                              sx={styles.navigationButton}
                              onClick={(): void => setSelectedImageIndex(selectedImageIndex + 1)}
                            >
                              <Typography sx={styles.navigationButtonText}>次のファイル</Typography>
                              <ArrowForwardIosIcon sx={{ fontSize: '16px' }} />
                            </Button>
                          ) : (
                            <Box sx={styles.navigationPlaceholder} />
                          )}
                        </Box>
                        <Box
                          sx={{
                            ...styles.imageContainer,
                            flexDirection: 'column',
                            gap: '16px',
                            overflowY: 'auto',
                          }}
                        >
                          {/* getCurrentDisplayImages() はレンダー中にPDF変換を起動するため、
                              1レンダーにつき1回だけ呼ぶ（複数回呼ぶと変換も多重に走る） */}
                          {displayImages.length > 0 ? (
                            displayImages.map((image, index) => (
                              <img
                                key={index}
                                src={image.dataUrl}
                                alt={`アップロード画像 ページ${image.page}`}
                                style={{ maxWidth: '100%', objectFit: 'contain' }}
                              />
                            ))
                          ) : getCurrentFileType() === 'pdf' ? (
                            <Typography sx={styles.noImageText}>
                              {pdfConvertFailedFiles.has(getCurrentFileName())
                                ? 'PDFのプレビューを表示できませんでした。ページを再読み込みしてお試しください。'
                                : 'PDF変換中...'}
                            </Typography>
                          ) : (
                            <Typography sx={styles.noImageText}>
                              ファイルをアップロードして選択してください
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* 右側：ItemListManager */}
                    <Box sx={styles.itemListColumn}>
                      <ItemListManager
                        control={control}
                        errors={itemListValidated ? errors : {}}
                        setValue={setValue}
                        trigger={trigger}
                        watch={watch}
                        disabled={!isOperatorStaff(user?.role) && useCustomPrompt}
                        titleShow={false}
                        fieldName="itemList"
                        validated={itemListValidated}
                        existingItemIds={existingItemIds}
                        onReRead={canUseAiItemRead ? handleReReadItemListRequest : undefined}
                        onResetRequest={isEditMode ? handleResetItemListRequest : undefined}
                        onDeleteRequest={handleItemListDeleteRequest}
                        onItemLimitExceeded={(): void => {
                          // 上限判定・メッセージはOCR対象項目数で行う（#3045）
                          const itemList = watch('itemList');
                          const ocrTargetCount = getOcrTargetItemCount(itemList);
                          setErrorText(getItemCountExceededMessage(ocrTargetCount));
                          setSnackbarOpen(true);
                        }}
                        onItemInserted={(): void => {
                          setItemListValidated(true);
                        }}
                        canImportJson={isOperatorStaff(user?.role)}
                        canCopyJson={isOperatorStaff(user?.role)}
                        // #3039: 既存パターンの編集中はJSONを初期表示し、項目IDの整合を検証する
                        isEditMode={!!currentPatternId}
                        onShowMessage={(message: string): void => {
                          setInfoText(message);
                          setSnackbarOpen(true);
                        }}
                        // #3344: JSON取り込みで照合キーの項目が消される場合に弾くため渡す
                        patternMasters={patternMasters}
                        patternDictionaries={patternDictionaries}
                      />
                    </Box>
                  </Box>

                  {/* 項目を追加ボタン（SPのみカード外に表示） */}
                  {(isOperatorStaff(user?.role) || !useCustomPrompt) && (
                    <Box
                      sx={{
                        display: 'none',
                        '@media screen and (max-width: 960px)': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginTop: '16px',
                          marginLeft: '16px',
                          marginRight: '16px',
                          height: '48px',
                          border: `1px solid ${orange50}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(240, 60, 10, 0.05)',
                          },
                        },
                      }}
                      onClick={handleAddItem}
                    >
                      <Typography sx={{ fontSize: '16px', color: orange50, fontWeight: 600 }}>
                        ＋
                      </Typography>
                      <Typography
                        sx={{
                          marginLeft: '4px',
                          fontSize: '16px',
                          color: orange50,
                          fontWeight: 600,
                        }}
                      >
                        項目を追加
                      </Typography>
                    </Box>
                  )}

                  {/* カスタムプロンプト設定（運営のみ） */}
                  {isOperatorStaff(user?.role) && (
                    <Box
                      sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '4px',
                        border: `1px solid ${gray30}`,
                        padding: '24px 32px',
                        marginTop: '32px',
                        marginLeft: '32px',
                        marginRight: '32px',
                        boxSizing: 'border-box',
                        '@media (max-width: 1200px)': {
                          marginLeft: '16px',
                          marginRight: '16px',
                          padding: '16px',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          sx={{
                            ...styles.subSectionTitle,
                            margin: 0,
                            paddingLeft: 0,
                            fontSize: '18px',
                            '&::before': { display: 'none' },
                          }}
                        >
                          カスタムプロンプト
                        </Typography>
                        <Switch
                          checked={useCustomPrompt}
                          onChange={(e): void => {
                            setUseCustomPrompt(e.target.checked);
                            setValue('useCustomPrompt', e.target.checked);
                            // ONにした時は入力欄を自動的に表示
                            if (e.target.checked) {
                              setShowPromptInput(true);
                            }
                          }}
                        />
                      </Box>
                      {useCustomPrompt && (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              marginBottom: '16px',
                            }}
                          >
                            <Button
                              sx={{
                                ...tertiaryButtonBackgroundColor,
                                height: '32px',
                                padding: '4px 8px',
                                minWidth: '80px',
                              }}
                              onClick={(): void => {
                                const itemList = watch('itemList');
                                if (itemList && itemList.length > 0) {
                                  const docName = watch('documentName') || undefined;
                                  const jsonForGeminiPrompt =
                                    convertItemListToGeminiPrompt(itemList);
                                  const jsonString = JSON.stringify(jsonForGeminiPrompt, null, 2);
                                  const dynamicPrompt = generatePromptFromItemList(
                                    itemList,
                                    docName,
                                  );
                                  const fullPrompt =
                                    getFormatDataPromptForPattern(docName) +
                                    '\n```json\n' +
                                    jsonString +
                                    '\n```\n\n## 項目取得ルール\n\n' +
                                    dynamicPrompt;
                                  setValue('prompt', fullPrompt);
                                }
                              }}
                            >
                              <Typography sx={{ ...boldMd, color: gray80 }}>
                                ベースプロンプト作成
                              </Typography>
                            </Button>
                            <Button
                              sx={{
                                height: '32px',
                                padding: '4px 8px',
                                minWidth: '80px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: gray80,
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                },
                              }}
                              onClick={(): void => setShowPromptInput(!showPromptInput)}
                            >
                              <Typography sx={{ ...boldMd, color: gray80 }}>
                                {showPromptInput ? '非表示' : '表示'}
                              </Typography>
                              <ExpandMoreIcon
                                sx={{
                                  color: gray80,
                                  transform: showPromptInput ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.3s',
                                }}
                              />
                            </Button>
                          </Box>
                          <Collapse in={showPromptInput}>
                            <Controller
                              render={({ field }): JSX.Element => (
                                <CustomTextfield
                                  {...field}
                                  error={Boolean(errors.prompt)}
                                  formMb="0px"
                                  helperText={errors.prompt?.message}
                                  labelText=""
                                  inputWidth="100%"
                                  height="450px"
                                  multiline
                                  rows={18}
                                />
                              )}
                              control={control}
                              name="prompt"
                            />
                          </Collapse>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* 固定フッター：戻る・次へボタン（Step 2のみ） */}
              {activeStep === 1 && (
                <Box sx={[styles.fixedFooter, { left: sideBarCollapsed ? '80px' : '240px' }]}>
                  <Button sx={styles.secondaryFooterButton} onClick={handleBack}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: gray80 }}>
                      戻る
                    </Typography>
                  </Button>
                  <Button
                    sx={styles.primaryFooterButton}
                    onClick={handleNext}
                    disabled={((): boolean => {
                      return itemListValidated && !!errors.itemList;
                    })()}
                  >
                    <Typography sx={{ ...boldSm, color: white }}>設定・保存へ</Typography>
                  </Button>
                </Box>
              )}

              {/* Step 3: 設定・保存セクション */}
              {activeStep === 2 && (
                <Box sx={styles.mainContainer}>
                  <Box
                    sx={{
                      marginBottom: '16px',
                      '& > div > div:first-of-type': {
                        '& > span:first-of-type': {
                          color: grayBlue50,
                          fontSize: '16px',
                          fontWeight: 700,
                        },
                      },
                    }}
                  >
                    <Controller
                      render={({ field }): JSX.Element => (
                        <CustomTextfield
                          {...field}
                          required
                          requiredText={'必須'}
                          error={Boolean(errors.title)}
                          formMb="0px"
                          height="40px"
                          helperText={errors.title?.message}
                          labelText="読取パターンの名前"
                          description="サイドバーやパターン一覧での表示名です。自由に名前をつけてください。"
                          inputWidth="100%"
                          borderColor={gray40}
                          inputPaddingIs10px8px
                        />
                      )}
                      control={control}
                      name="title"
                    />
                  </Box>
                  <Box
                    sx={{
                      marginBottom: '16px',
                      '& > div > div:first-of-type': {
                        '& > span:first-of-type': {
                          color: grayBlue50,
                          fontSize: '16px',
                          fontWeight: 700,
                        },
                      },
                    }}
                  >
                    <Controller
                      render={({ field }): JSX.Element => (
                        <CustomTextfield
                          {...field}
                          value={field.value || ''}
                          error={Boolean(errors.documentName)}
                          formMb="0px"
                          height="40px"
                          helperText={errors.documentName?.message}
                          labelText="書類の名称"
                          description="項目の読み取り時にAIが参照します。対象の書類名を正確に入力してください（例：請求書、納品書、見積書）。"
                          inputWidth="100%"
                          borderColor={gray40}
                          inputPaddingIs10px8px
                        />
                      )}
                      control={control}
                      name="documentName"
                    />
                  </Box>
                  <Box
                    sx={{
                      marginBottom: '16px',
                      '& > div > div:first-of-type': {
                        marginBottom: '2px',
                        '& > span:first-of-type': {
                          color: grayBlue50,
                          fontSize: '16px',
                          fontWeight: 700,
                        },
                      },
                    }}
                  >
                    <Controller
                      render={({ field }): JSX.Element => (
                        <CustomTextfield
                          {...field}
                          value={field.value || ''}
                          error={Boolean(errors.description)}
                          formMb="0px"
                          helperText={errors.description?.message}
                          labelText="書類の説明"
                          description="この読取パターンが対象とする書類の特徴・用途を記入してください（例:「ご請求額」や「お振込先」が記載された請求書類）"
                          inputWidth="100%"
                          height="auto"
                          multiline
                          rows={3}
                          borderColor={gray40}
                          inputPaddingIs10px8px
                        />
                      )}
                      control={control}
                      name="description"
                    />
                  </Box>
                  <Box sx={{ marginBottom: '16px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          sx={{
                            color: grayBlue50,
                            fontSize: '16px',
                            fontWeight: 700,
                          }}
                        >
                          自動仕分の対象にする
                        </Typography>
                        <Switch
                          checked={isClassificationTarget}
                          onChange={(e): void => setIsClassificationTarget(e.target.checked)}
                        />
                      </Box>
                      <Link
                        component="button"
                        type="button"
                        onClick={(): void => {
                          window.open(HELP_FAQ_AUTO_CLASSIFICATION, '_blank');
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '14px',
                          fontWeight: 700,
                          color: primaryButtonBackgroundColor.backgroundColor,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        <HelpOutlineOutlined sx={{ fontSize: '16px' }} />
                        {AUTO_CLASSIFY_HELP_LABEL}
                      </Link>
                    </Box>
                    <Typography
                      sx={{
                        color: gray80,
                        fontSize: '12px',
                        textAlign: 'left',
                      }}
                    >
                      AIが書類を自動で仕分けて、種類ごと（請求書・納品書など）に読取する機能です。
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      marginBottom: '16px',
                      '& > div > div:first-of-type': {
                        '& > span:first-of-type': {
                          color: grayBlue50,
                          fontSize: '16px',
                          fontWeight: 700,
                        },
                      },
                    }}
                  >
                    <Controller
                      render={({ field }): JSX.Element => (
                        <CustomTextfield
                          {...field}
                          error={Boolean(errors.remarks)}
                          formMb="0px"
                          helperText={errors.remarks?.message}
                          labelText="備考"
                          inputWidth="100%"
                          height="auto"
                          multiline
                          rows={3}
                          borderColor={gray40}
                          inputPaddingIs10px8px
                        />
                      )}
                      control={control}
                      name="remarks"
                    />
                  </Box>
                  <Divider sx={{ borderColor: gray40, margin: '16px 0' }} />

                  {/* 共有範囲（#2804 Phase2）: グループ未選択=企業全体 / 選択=そのグループ限定 */}
                  <Box sx={{ padding: '8px 0' }}>
                    <Typography
                      sx={{
                        color: grayBlue50,
                        fontSize: '16px',
                        fontWeight: 700,
                        mb: '4px',
                        textAlign: 'left',
                      }}
                    >
                      共有グループ
                    </Typography>
                    <Typography
                      sx={{ color: gray80, fontSize: '12px', mb: '8px', textAlign: 'left' }}
                    >
                      限定共有するグループを選択します。
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    >
                      {availableGroups.length === 0 ? (
                        <Typography
                          sx={{
                            color: gray80,
                            fontSize: '12px',
                            padding: '16px',
                            textAlign: 'center',
                          }}
                        >
                          {GROUP_EMPTY_MESSAGE}
                        </Typography>
                      ) : (
                        availableGroups.map(g => (
                          <Box
                            key={g.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              // チェックボックスをラベル「共有グループ」の左端に揃えるため左パディングは持たせない（#2804）
                              padding: '4px 0',
                              cursor: 'pointer',
                            }}
                            onClick={(): void =>
                              setSelectedSharedGroupIds(prev =>
                                prev.includes(g.id)
                                  ? prev.filter(id => id !== g.id)
                                  : [...prev, g.id],
                              )
                            }
                          >
                            <Checkbox
                              checked={selectedSharedGroupIds.includes(g.id)}
                              // 内部パディング分を負マージンで打ち消し、チェック枠の左端をラベル左端に合わせる
                              sx={{
                                '&.Mui-checked': { color: orange50 },
                                padding: '4px',
                                marginLeft: '-4px',
                              }}
                            />
                            <Typography sx={{ fontSize: '14px', color: gray100 }}>
                              {g.name}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>

                  {/* 共有担当者（#2963）: 担当者個人を共有先に指定。グループとの併用可（OR） */}
                  <Box sx={{ padding: '8px 0' }}>
                    <Typography
                      sx={{
                        color: grayBlue50,
                        fontSize: '16px',
                        fontWeight: 700,
                        mb: '4px',
                        textAlign: 'left',
                      }}
                    >
                      共有担当者
                    </Typography>
                    <Typography
                      sx={{ color: gray80, fontSize: '12px', mb: '8px', textAlign: 'left' }}
                    >
                      限定共有する担当者を選択します。グループ・担当者のいずれも選択しない場合は全体に共有されます。
                      <br />
                      限定共有では、作成・編集したご自身は自動的に共有先に含まれます。
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    >
                      {availableUsers.length === 0 ? (
                        <Typography
                          sx={{
                            color: gray80,
                            fontSize: '12px',
                            padding: '16px',
                            textAlign: 'center',
                          }}
                        >
                          共有できる担当者がいません
                        </Typography>
                      ) : (
                        availableUsers.map(u => (
                          <Box
                            key={u.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '4px 0',
                              cursor: 'pointer',
                            }}
                            onClick={(): void =>
                              setSelectedSharedUserIds(prev =>
                                prev.includes(u.id)
                                  ? prev.filter(id => id !== u.id)
                                  : [...prev, u.id],
                              )
                            }
                          >
                            <Checkbox
                              checked={selectedSharedUserIds.includes(u.id)}
                              sx={{
                                '&.Mui-checked': { color: orange50 },
                                padding: '4px',
                                marginLeft: '-4px',
                              }}
                            />
                            <Typography sx={{ fontSize: '14px', color: gray100 }}>
                              {u.name}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>

                  {/* 指定フォーマット出力（#3070）。
                      運営が企業ごとに有効化した企業のユーザーが自分で登録できる（#3207）。
                      仕様書 §4 のとおり利用条件は enable_export_template のみで、
                      権限による出し分けはしない。無効な企業では区切り線も出さない */}
                  {company?.enable_export_template && (
                    <>
                      <Divider sx={{ borderColor: gray40, margin: '16px 0' }} />
                      <Box sx={{ padding: '8px 0' }}>
                        <ExportTemplateManager
                          patternId={currentPatternId ?? undefined}
                          companyId={effectiveCompanyId ?? ''}
                          itemList={watch('itemList') as ItemListType[]}
                          onShowMessage={(message): void => {
                            setInfoText(message);
                            setSnackbarOpen(true);
                          }}
                          onChange={setPendingExportTemplates}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* 固定フッター：戻る・登録/更新ボタン（Step 2） */}
              {activeStep === 2 && (
                <Box sx={[styles.fixedFooter, { left: sideBarCollapsed ? '80px' : '240px' }]}>
                  <Button sx={styles.secondaryFooterButton} onClick={handleBack}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: gray80 }}>
                      戻る
                    </Typography>
                  </Button>
                  {!isDeletedPattern && (
                    <Button
                      sx={styles.primaryFooterButton}
                      disabled={buttonDisabledFlag()}
                      onClick={async (): Promise<void> => {
                        // 初回押下を記録（バリデーションエラー時にボタンを無効化するため）
                        setIsSubmitAttempted(true);
                        await handleSubmit(async () => onSave(), onError)();
                      }}
                    >
                      <Typography sx={{ ...boldSm, color: white }}>
                        <Box
                          component="span"
                          sx={{ '@media (max-width: 600px)': { display: 'none' } }}
                        >
                          読取パターンを
                        </Box>
                        {currentPatternId ? '更新する' : '登録する'}
                      </Typography>
                    </Button>
                  )}
                  {isDeletedPattern && (
                    <Typography sx={{ fontSize: '13px', color: '#9e9e9e' }}>
                      このパターンは削除済みです。閲覧のみ可能です。
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <CustomLoading loading={loading} />

      <CustomSnackbar
        autoHideDuration={loading ? null : undefined}
        open={snackbarOpen}
        onClose={(): void => {
          if (!loading) {
            setSnackbarOpen(false);
          }
        }}
        text={errorText}
        infoText={infoText}
      />

      {/* AI項目読取中のローディングモーダル */}
      <LoadingModal
        open={itemReadingModalOpen}
        progress={itemReadingProgress}
        message={itemReadingMessage}
      />

      {/* 編集時に項目設定が丸ごと置き換わる操作（AI読取・リセット）の確認モーダル（#3147） */}
      <PatternItemOverwriteConfirmModal
        open={itemOverwriteConfirmTarget !== null}
        mode={itemOverwriteConfirmTarget === 'reset' ? 'reset' : 'aiRead'}
        onClose={(): void => setItemOverwriteConfirmTarget(null)}
        onConfirm={(): void => {
          void handleItemOverwriteConfirm();
        }}
      />

      {/* ItemListManager用削除確認モーダル */}
      <ItemDeleteModal
        open={itemListDeleteModalOpen}
        onClose={handleItemListDeleteClose}
        onDelete={handleItemListDeleteConfirm}
        itemData={
          itemListDeleteItemData
            ? {
                id: itemListDeleteItemData.id,
                itemName: itemListDeleteItemData.itemName,
                isChild: itemListDeleteItemData.isChild,
              }
            : null
        }
        customMessage={
          <>
            {/* マスタ照合・辞書変換から参照されている場合のみ、影響先を列挙する（#3344） */}
            {itemListDeleteReferences.length > 0 && (
              <>
                この項目は以下の設定に使われています。削除すると設定から外れます。
                <br />
                {formatItemReferences(itemListDeleteReferences).map(reference => (
                  <React.Fragment key={reference}>
                    ・{reference}
                    <br />
                  </React.Fragment>
                ))}
                <br />
              </>
            )}
            項目を削除するとこれまで読み取ったデータが表示されなくなりますがよろしいですか。
            <br />
            <br />
            ※削除後に同じ名称の項目を追加しても新しい項目とみなされます。これまでに読み取ったデータは引き継がれないのでご注意ください。
          </>
        }
      />

      {/* 照合キーとして使われている項目の削除をブロックする案内（#3344） */}
      <ItemDeleteBlockedModal
        open={itemDeleteBlockedModalOpen}
        onClose={handleItemDeleteBlockedClose}
        itemName={itemListDeleteItemData?.itemName ?? ''}
        references={formatItemReferences(getBlockingReferences(itemListDeleteReferences))}
      />

      {/* 保存完了モーダル */}
      <PatternCompleteModal
        open={patternCompleteModalOpen}
        patternId={id || currentPatternId || ''}
        uploadedFiles={uploadedFilesForOcr.length > 0 ? uploadedFilesForOcr : undefined}
        onOcrStart={(): void => {
          if (!user || !company || baseRemainingCount === null) return;
          const resolvedPatternId = id || currentPatternId || '';
          const watchedData = watch();
          // 担当者（#2642 / #2928）: パターン作成直後の初回OCR。
          // 通常モードはアップロード者本人。代理モードは運営ユーザー（代理先の非メンバー）を
          // 担当者にすると一覧で「退会済ユーザー」になるため、代理先の企業担当者を使う
          // （取得できない場合は未設定）。
          const initialAssignee = isProxyMode
            ? proxyDefaultAssignee
            : { uid: user.uid, name: user.name || '', iconUrl: user.icon ?? null };
          startUpload({
            type: 'pattern',
            files: uploadedFilesForOcr,
            originalFiles: uploadedFilesForOcr,
            totalPageCount: uploadedFilesForOcr.length,
            pageFileNames: uploadedFilesForOcr.map(f => f.name),
            userId: user.uid,
            userName: user.name || '',
            userIconUrl: user.icon,
            assigneeId: initialAssignee?.uid ?? null,
            assigneeName: initialAssignee?.name ?? null,
            assigneeIconUrl: initialAssignee?.iconUrl ?? null,
            processingType: watchedData.fileType,
            geminiModel: selectedGeminiModel,
            companyId: effectiveCompanyId ?? user.company_id ?? undefined,
            allowAiLearning,
            isProxyMode,
            operatorId: isProxyMode ? (operatorId ?? undefined) : undefined,
            operatorRole: isProxyMode ? (operatorRole ?? undefined) : undefined,
            company,
            baseRemainingCount,
            patternId: resolvedPatternId,
            patternName: watchedData.title || resolvedPatternId,
            itemList: watchedData.itemList,
            hidePanel: true,
          });
        }}
        onClose={(): void => {
          setPatternCompleteModalOpen(false);
          // 新規作成時はURLをedit形式に変更（再保存で二重登録されないようにする）
          if (!id && currentPatternId) {
            navigate(`/pattern/${currentPatternId}`, { replace: true });
          }
          // current_pattern_count はリアルタイムリスナーが自動更新するため手動更新不要
        }}
      />

      {/* チュートリアルモーダル */}
      <TutorialModal
        open={tutorialModalOpen}
        onClose={(): void => setTutorialModalOpen(false)}
        onComplete={(): void => setTutorialModalOpen(false)}
      />
    </Box>
  );
};

export default PatternAdd;
